import { useEffect, useState, useCallback, Suspense, lazy, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTaxFilingReadiness } from '../hooks/useTaxFilingReadiness';
import { useTaxLots } from '../hooks/useTaxLots';
import { useCurrencyDisplay } from '../hooks/useCurrencyDisplay';
import type { CostBasisMethod, TaxLotView } from '../api/taxLots';
import { getIncomeEvents, type IncomeEventsResponse, getTaxReports, type TaxReport } from '../api/transactions';
import { useAppSelector } from '../store/hooks';
import apiClient from '../api/client';
import { TaxExportPanel, type TaxExportPanelSnapshot } from '../components/tax/TaxExportPanel';
import TaxCompletionRail from '../components/tax/TaxCompletionRail';
import TaxNextStepArrivalRail from '../components/tax/TaxNextStepArrivalRail';
import CarriedSourceFreshnessRail from '../components/common/CarriedSourceFreshnessRail';
import Button from '../components/common/Button';
import InlineStatus from '../components/common/InlineStatus';
import {
  getHarvestingOpportunities,
  getHarvestingSummary,
  type HarvestingOpportunitiesPayload,
  type HarvestingSummary,
} from '../api/taxHarvesting';
import {
  getTaxExportSummary,
  type TaxExportSummary,
  type TaxExportOperationStatus,
} from '../api/tax-export';
import { getSafeTaxExportYear, readPersistedTaxExportYear } from '../lib/taxExportYear';
import {
  captureAuthReturnContext,
  getAuthRecoveryStateFromSearch,
  stripAuthRecoveryParams,
} from '../lib/authReturnContext';
import {
  getTaxNextStepLabel,
  getTaxNextStepPath,
  readTaxNextStepHandoff,
  type TaxNextStep,
  type TaxNextStepFocus,
  type TaxNextStepHandoffContext,
} from '../lib/taxNextStepHandoff';
import { formatSourceProofOccurredAt } from '../lib/sourceProof';
import {
  persistTaxSourceProof,
  readPersistedTaxSourceProof,
  readTaxSourceProofFromSearchParams,
  type TaxSourceProofSnapshot,
} from '../lib/taxSourceProof';
import { buildSourceFreshnessFromTaxSourceProof } from '../lib/sourceFreshnessHandoff';
import {
  getTaxFilingOwnerHeadline,
  getTaxFilingTruthChipLabel,
  getTaxFilingTruthStage,
} from '../lib/taxFilingTruth';
import { getRecommendationTransactionsHandoffPath } from '../lib/recommendationTransactionsHandoff';

// Lazy-load sibling tax pages so they are embedded as tabs
const TaxHarvestPage = lazy(() => import('./TaxHarvestPage'));
const TaxSimulatorPage = lazy(() => import('./TaxSimulatorPage'));

// ─── Top-level tab definition ─────────────────────────────────────────────────
type TopTab = 'overview' | 'harvesting' | 'simulator' | 'export';

const TOP_TABS: { key: TopTab; label: string }[] = [
  { key: 'overview',   label: 'Overview'             },
  { key: 'harvesting', label: 'Tax-Loss Harvesting'  },
  { key: 'simulator',  label: 'Simulator'             },
  { key: 'export', label: 'Export' },
];
const TOP_TAB_LABELS: Record<TopTab, string> = {
  overview: 'Overview',
  harvesting: 'Tax-Loss Harvesting',
  simulator: 'Simulator',
  export: 'Export',
};
import {
  submitTaxFiling,
  downloadCsvFiling,
  getFilingStatus,
  getFilingHistory,
  TaxFilingApiError,
  type CsvTransaction,
  type TaxFilingRecord,
  type FilingProvider,
  type FilingStatus,
  type TaxFilingReadinessActionLabel,
  type TaxFilingReadinessPayload,
} from '../api/tax-filing';

// ── Icons ────────────────────────────────────────────────────────────────────

const ScalesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 2v16M4 6l-2 4h4L4 6zM16 6l-2 4h4l-2-4z" />
    <path d="M2 18h16" />
    <path d="M7 18h6" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 11V3M3 7l4-4 4 4" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 3v8M11 7l-4 4-4-4" />
  </svg>
);

const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="6" cy="6" r="5" />
    <path d="M6 3.5V6l2 1.5" />
  </svg>
);

function TaxSurfaceLoadingState({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-6 space-y-4">
      <div className="space-y-2">
        <div className="skeleton h-5 w-40" />
        <div className="skeleton h-4 w-72" />
      </div>
      <div className="grid gap-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="skeleton h-12 w-full rounded-lg" />
        ))}
      </div>
      <p className="text-sm text-text-muted">{label} is loading. Your selected tab stays in place while this content prepares.</p>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

type FormatCurrencyFn = (n: number) => string;

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '–';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatMethodLabel(method: CostBasisMethod): string {
  return method.toUpperCase();
}

function formatUsdValue(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface DominantTaxRail {
  step: TaxNextStep;
  tab: 'overview' | 'harvesting' | 'export';
  focus: TaxNextStepFocus;
  title: string;
  message: string;
  ctaLabel: string;
  proofLabel: string;
  proofCount?: number;
  proofValue?: string;
  year?: number;
  secondaryActions: Array<{
    key: string;
    label: string;
    tab: TopTab;
  }>;
}

// ── Method selector ──────────────────────────────────────────────────────────

const METHODS: { value: CostBasisMethod; label: string; description: string }[] = [
  { value: 'fifo', label: 'FIFO', description: 'First In, First Out' },
  { value: 'lifo', label: 'LIFO', description: 'Last In, First Out' },
  { value: 'hifo', label: 'HIFO', description: 'Highest In, First Out' },
];

// ── Jurisdiction constants ────────────────────────────────────────────────────

type TaxJurisdiction = 'US' | 'UK' | 'AU' | 'EU_DE' | 'EU_FR' | 'EU_OTHER';

const JURISDICTION_OPTIONS: { value: TaxJurisdiction; label: string }[] = [
  { value: 'US', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'EU_DE', label: 'Germany' },
  { value: 'EU_FR', label: 'France' },
  { value: 'EU_OTHER', label: 'Other (EU)' },
];

const JURISDICTION_NOTES: Record<TaxJurisdiction, string> = {
  US: 'US: Short-term gains (<1 year) taxed as ordinary income; long-term gains (≥1 year) at preferential rates. Wash-sale rules apply within 30 days.',
  UK: 'UK: Section 104 pooling with 30-day bed-and-breakfasting rule applied. Capital Gains Tax Annual Exempt Amount not deducted — consult HMRC.',
  AU: 'AU: FIFO with 50% CGT discount for assets held >12 months. Losses are applied against gains before discounting. Consult the ATO for your personal tax situation.',
  EU_DE: 'Germany: Gains on assets held ≥1 year are tax-free under §23 EStG. Short-term gains taxed at your personal income rate. €600 annual exemption not deducted here — consult a Steuerberater.',
  EU_FR: 'France: Flat PFU rate of 30% applies to net crypto gains (12.8% income tax + 17.2% social contributions). Losses in the same year offset gains. Consult a conseiller fiscal.',
  EU_OTHER: 'EU (other): Generic FIFO with a 1-year short/long-term distinction. Rules vary significantly by country — consult your national tax authority.',
};

const TAX_FILING_PROVIDER_STORAGE_KEY = 'pinecurl.tax-filing.provider';
const TAX_FILING_YEAR_STORAGE_KEY = 'pinecurl.tax-filing.selected-year';
const FILING_YEAR_OPTIONS = [2022, 2023, 2024, 2025];
const FILING_PROVIDER_OPTIONS: Array<{
  value: FilingProvider;
  label: string;
  detail: string;
  icon: string;
}> = [
  {
    value: 'turbotax',
    label: 'TurboTax',
    detail: 'Submit with mounted export proof',
    icon: '🇺🇸',
  },
  {
    value: 'hmrc',
    label: 'HMRC MTD',
    detail: 'Submit after adding an HMRC token',
    icon: '🇬🇧',
  },
  {
    value: 'csv',
    label: 'CSV download',
    detail: 'Download-only file from a stored report',
    icon: '📄',
  },
];

function readPersistedTaxFilingProvider(): FilingProvider {
  if (typeof window === 'undefined') {
    return 'turbotax';
  }

  const value = window.localStorage.getItem(TAX_FILING_PROVIDER_STORAGE_KEY);
  return value === 'hmrc' || value === 'csv' || value === 'turbotax' ? value : 'turbotax';
}

function readPersistedTaxFilingYear(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(TAX_FILING_YEAR_STORAGE_KEY);
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatTaxFilingProviderLabel(provider: FilingProvider): string {
  switch (provider) {
    case 'hmrc':
      return 'HMRC';
    case 'csv':
      return 'CSV';
    default:
      return 'TurboTax';
  }
}

function formatFilingStatusChip(status: FilingStatus): string {
  switch (status) {
    case 'accepted':
      return 'Accepted';
    case 'submitted':
      return 'Submitted';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Pending';
  }
}

function sortFilingRecordsByRecency(left: TaxFilingRecord, right: TaxFilingRecord): number {
  return (
    new Date(right.updatedAt ?? right.createdAt).getTime()
    - new Date(left.updatedAt ?? left.createdAt).getTime()
  );
}

function sortTaxReportsByRecency(left: TaxReport, right: TaxReport): number {
  return new Date(right.generatedAt).getTime() - new Date(left.generatedAt).getTime();
}

function buildCsvTransactionsFromReport(report: TaxReport): CsvTransaction[] {
  return report.gains.map((gain) => ({
    date: gain.saleDate,
    sentAmount: Number(gain.saleAmount),
    sentCurrency: gain.symbol,
    receivedAmount: Number(gain.totalProceeds),
    receivedCurrency: 'USD',
    feeAmount: 0,
    feeCurrency: 'USD',
    tag: 'trade',
  }));
}

function getFilingReadinessHeadline(readiness: TaxFilingReadinessPayload): string {
  return getTaxFilingOwnerHeadline(readiness);
}

function getFilingReadinessProofAt(
  readiness: TaxFilingReadinessPayload | null,
  csvDownloadProof?: { year: number; downloadedAt: string } | null,
): string | null {
  if (!readiness) {
    return csvDownloadProof?.downloadedAt ?? null;
  }

  return (
    (csvDownloadProof?.year === readiness.year ? csvDownloadProof.downloadedAt : null)
    ?? readiness.proof.filing.submittedAt
    ?? readiness.proof.filing.updatedAt
    ?? readiness.proof.export.completedAt
    ?? readiness.proof.export.updatedAt
    ?? readiness.proof.report.generatedAt
    ?? readiness.proof.lots.updatedAt
    ?? null
  );
}

interface LatestExportActionSnapshot {
  action: string;
  state: TaxExportOperationStatus['state'] | 'blocked' | null;
  updatedAt?: string;
  completedAt?: string;
  startedAt?: string;
  fileName?: string;
  message?: string;
  error?: string;
  appliedCurrency?: TaxExportPanelSnapshot['selectedCurrency'];
  requestedCurrency?: TaxExportPanelSnapshot['selectedCurrency'];
  blockerCode?: string;
}

function getLatestExportAction(
  snapshot: TaxExportPanelSnapshot | null,
  states: Array<TaxExportOperationStatus['state']>,
): LatestExportActionSnapshot | null {
  if (!snapshot) {
    return null;
  }

  const matches: LatestExportActionSnapshot[] = [];

  Object.entries(snapshot.actions).forEach(([action, value]) => {
    if (!value || !states.includes(value.state as TaxExportOperationStatus['state'])) {
      return;
    }

    matches.push({
      action,
      ...value,
    });
  });

  matches.sort((left, right) => {
    const rightTime = new Date(right.completedAt ?? right.updatedAt ?? right.startedAt ?? 0).getTime();
    const leftTime = new Date(left.completedAt ?? left.updatedAt ?? left.startedAt ?? 0).getTime();
    return rightTime - leftTime;
  });

  return matches[0] ?? null;
}

// ── Lot row ──────────────────────────────────────────────────────────────────

function LotRow({ lot, rank, formatCurrency }: { lot: TaxLotView; rank: number; formatCurrency: FormatCurrencyFn }) {
  const gainPositive = (lot.unrealisedGainLoss ?? 0) >= 0;
  const fmtVal = (n: number | null | undefined) => n == null ? '–' : formatCurrency(n);
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
      <td className="py-3 px-4 text-sm text-white/40 font-mono">{rank}</td>
      <td className="py-3 px-4">
        <span className="text-sm font-semibold text-white">{lot.symbol}</span>
      </td>
      <td className="py-3 px-4 text-sm text-white/80 font-mono">{lot.amount.toFixed(6)}</td>
      <td className="py-3 px-4 text-sm text-white/80">{fmtVal(lot.costBasisPerUnit)}</td>
      <td className="py-3 px-4 text-sm text-white/80">{fmtVal(lot.totalCostBasis)}</td>
      <td className="py-3 px-4 text-sm text-white/80">{fmtVal(lot.currentValueUsd)}</td>
      <td className="py-3 px-4">
        <span className={`flex items-center gap-1 text-sm font-medium ${gainPositive ? 'text-emerald-400' : 'text-red-400'}`}>
          {gainPositive ? <ArrowUpIcon /> : <ArrowDownIcon />}
          {fmtVal(lot.unrealisedGainLoss)}
          <span className="text-white/40 font-normal">({fmtPct(lot.unrealisedGainLossPct)})</span>
        </span>
      </td>
      <td className="py-3 px-4">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            lot.holdingPeriod === 'LONG_TERM'
              ? 'bg-blue-500/15 text-blue-300'
              : 'bg-amber-500/15 text-amber-300'
          }`}
        >
          <ClockIcon />
          {lot.holdingPeriod === 'LONG_TERM' ? 'Long-term' : 'Short-term'}
          <span className="text-white/40">({lot.holdingDays}d)</span>
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-white/50">{fmtDate(lot.dateAcquired)}</td>
    </tr>
  );
}

// ── Comparison card ──────────────────────────────────────────────────────────

function ComparisonCard({
  method,
  totalUnrealisedGainLoss,
  isLoading,
  isActive,
  formatCurrency,
  onSelect,
}: {
  method: { value: CostBasisMethod; label: string; description: string };
  totalUnrealisedGainLoss: number | null;
  isLoading: boolean;
  isActive: boolean;
  formatCurrency: FormatCurrencyFn;
  onSelect: () => void;
}) {
  const gain = totalUnrealisedGainLoss ?? 0;
  const positive = gain >= 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Select ${method.label} cost basis method`}
      aria-pressed={isActive}
      className={`w-full rounded-xl border p-5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 ${
        isActive
          ? 'border-violet-500/50 bg-violet-500/10'
          : 'border-white/10 bg-white/[0.03] hover:border-violet-400/30 hover:bg-white/[0.05]'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-white/50 font-medium uppercase tracking-wider">{method.description}</p>
          <p className={`text-xl font-bold mt-0.5 ${isActive ? 'text-violet-300' : 'text-white'}`}>
            {method.label}
          </p>
        </div>
        {isActive && (
          <span className="text-xs bg-violet-500/20 text-violet-300 border border-violet-500/30 rounded-full px-2.5 py-0.5">
            Active
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="h-8 w-28 rounded bg-white/5 animate-pulse" />
      ) : (
        <p className={`text-2xl font-bold ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
          {totalUnrealisedGainLoss == null ? '–' : formatCurrency(totalUnrealisedGainLoss)}
        </p>
      )}
      <p className="text-xs text-white/40 mt-1">Unrealised gain / loss</p>
      <p className={`mt-3 text-xs ${isActive ? 'text-violet-200/80' : 'text-white/45'}`}>
        {isActive
          ? 'Viewing this method in the lot ledger below.'
          : 'Click to preview this method in the lot ledger below.'}
      </p>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const INCOME_TYPE_LABELS: Record<string, string> = {
  staking_reward: 'Staking Rewards',
  airdrop: 'Airdrops',
  gift: 'Gifts',
  fork: 'Fork Distributions',
  mining: 'Mining',
  interest: 'Interest',
};

export default function TaxOptimisationPage() {
  const { user } = useAppSelector((s) => s.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const reviewMethodComparisonRef = useRef<HTMLElement | null>(null);
  const reviewLotLedgerRef = useRef<HTMLElement | null>(null);
  const filingSectionRef = useRef<HTMLElement | null>(null);
  const filingHistoryRef = useRef<HTMLDivElement | null>(null);

  // ── Tab state — URL-persisted via ?tab= query param ───────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TopTab | null;
  const activeTab: TopTab = rawTab && TOP_TABS.some((t) => t.key === rawTab) ? rawTab : 'overview';
  const taxHandoff = readTaxNextStepHandoff(searchParams);
  const authRecoveryState = getAuthRecoveryStateFromSearch(location.pathname, location.search);
  const setActiveTab = (key: TopTab) => {
    setSearchParams({ tab: key }, { replace: true });
  };
  const selectedOverviewExportYear = useMemo(
    () => getSafeTaxExportYear(taxHandoff?.year ?? readPersistedTaxExportYear()),
    [taxHandoff?.year],
  );
  const persistedFilingYear = useMemo(() => readPersistedTaxFilingYear(), []);

  const [activeMethod, setActiveMethod] = useState<CostBasisMethod>('fifo');
  const { formatValue, currency } = useCurrencyDisplay();
  const formatCurrency: FormatCurrencyFn = (n: number) => formatValue(n);

  // ── Jurisdiction state ───────────────────────────────────────────────────
  const [jurisdiction, setJurisdiction] = useState<TaxJurisdiction>(
    (user?.taxJurisdiction as TaxJurisdiction) ?? 'US',
  );
  const [jurisdictionSaving, setJurisdictionSaving] = useState(false);
  const [jurisdictionStatus, setJurisdictionStatus] = useState<{
    tone: 'info' | 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const handleJurisdictionChange = useCallback(
    async (newJurisdiction: TaxJurisdiction) => {
      if (newJurisdiction === jurisdiction) return;
      setJurisdiction(newJurisdiction);
      if (!user?.id) return;
      setJurisdictionSaving(true);
      setJurisdictionStatus({
        tone: 'info',
        title: 'Saving jurisdiction',
        message: `${newJurisdiction} tax rules are being saved to your profile. There is no need to change the selector again.`,
      });
      try {
        await apiClient.patch(`/auth/users/${user.id}/preferences`, {
          taxJurisdiction: newJurisdiction,
        });
        setJurisdictionStatus({
          tone: 'success',
          title: 'Jurisdiction saved',
          message: `${newJurisdiction} tax rules are now the saved default for this workspace.`,
        });
      } catch {
        setJurisdictionStatus({
          tone: 'error',
          title: 'Jurisdiction not saved',
          message: `We kept ${newJurisdiction} selected locally, but could not save it to your profile. Retry when the connection is stable.`,
        });
      } finally {
        setJurisdictionSaving(false);
      }
    },
    [jurisdiction, user?.id],
  );

  // Ordinary income state
  const [incomeYear, setIncomeYear] = useState<number>(new Date().getFullYear());
  const [incomeData, setIncomeData] = useState<IncomeEventsResponse | null>(null);
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [incomeError, setIncomeError] = useState<string | null>(null);

  // ── Tax Filing state ─────────────────────────────────────────────────────
  const [filingProvider, setFilingProvider] = useState<FilingProvider>(() => readPersistedTaxFilingProvider());
  const [filingYear, setFilingYear] = useState<number>(
    () => persistedFilingYear ?? selectedOverviewExportYear,
  );
  const [filingYearTouched, setFilingYearTouched] = useState(() => persistedFilingYear !== null);
  const [hmrcToken, setHmrcToken] = useState('');
  const [filingLoading, setFilingLoading] = useState(false);
  const [filingStatus, setFilingStatus] = useState<{
    tone: 'info' | 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);
  const [filingRecord, setFilingRecord] = useState<TaxFilingRecord | null>(null);
  const [filingHistory, setFilingHistory] = useState<TaxFilingRecord[]>([]);
  const [filingHistoryLoading, setFilingHistoryLoading] = useState(false);
  const [csvDownloadProof, setCsvDownloadProof] = useState<{
    year: number;
    downloadedAt: string;
    fileName: string;
  } | null>(null);
  const [pendingOverviewFocus, setPendingOverviewFocus] = useState<'filing-form' | 'filing-history' | null>(null);
  const [exportPanelSnapshot, setExportPanelSnapshot] = useState<TaxExportPanelSnapshot | null>(null);
  const [persistedSourceProof, setPersistedSourceProof] = useState<TaxSourceProofSnapshot | null>(null);
  const [pollingIntervalId, setPollingIntervalId] = useState<ReturnType<typeof setInterval> | null>(null);
  const [overviewHarvestSummary, setOverviewHarvestSummary] = useState<HarvestingSummary | null>(null);
  const [overviewHarvestSummaryLoading, setOverviewHarvestSummaryLoading] = useState(false);
  const [overviewHarvestSummaryError, setOverviewHarvestSummaryError] = useState<string | null>(null);
  const [overviewHarvestOpportunities, setOverviewHarvestOpportunities] = useState<HarvestingOpportunitiesPayload | null>(null);
  const [overviewExportSummary, setOverviewExportSummary] = useState<TaxExportSummary | null>(null);
  const [overviewExportSummaryLoading, setOverviewExportSummaryLoading] = useState(false);
  const [overviewExportSummaryError, setOverviewExportSummaryError] = useState<string | null>(null);
  const [taxReport, setTaxReport] = useState<TaxReport | null>(null);
  const [taxReports, setTaxReports] = useState<TaxReport[]>([]);
  const [taxReportLoading, setTaxReportLoading] = useState(false);
  const [taxReportError, setTaxReportError] = useState<string | null>(null);
  const {
    displayedReadiness: displayedFilingReadiness,
    loading: filingReadinessLoading,
    error: filingReadinessError,
    recovered: filingReadinessRecovered,
    rechecking: filingReadinessRechecking,
    empty: filingReadinessEmpty,
  } = useTaxFilingReadiness({
    userId: user?.id,
    year: filingYear,
    provider: filingProvider,
    routeKey: 'tax-optimisation',
  });

  // Load filing history on mount
  useEffect(() => {
    if (!user?.id) return;
    setFilingHistoryLoading(true);
    getFilingHistory(user.id)
      .then((res) => setFilingHistory([...res.filings].sort(sortFilingRecordsByRecency)))
      .catch(() => setFilingHistory([]))
      .finally(() => setFilingHistoryLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(TAX_FILING_PROVIDER_STORAGE_KEY, filingProvider);
  }, [filingProvider]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(TAX_FILING_YEAR_STORAGE_KEY, String(filingYear));
  }, [filingYear]);

  useEffect(() => {
    setPersistedSourceProof(readPersistedTaxSourceProof(user?.id));
  }, [user?.id]);

  useEffect(() => {
    if (filingYearTouched) {
      return;
    }

    setFilingYear(selectedOverviewExportYear);
  }, [filingYearTouched, selectedOverviewExportYear]);

  useEffect(() => {
    if (activeTab !== 'overview' || !pendingOverviewFocus) {
      return;
    }

    const focusTarget =
      pendingOverviewFocus === 'filing-history'
        ? filingHistoryRef.current
        : filingSectionRef.current;

    if (!focusTarget) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (typeof focusTarget.scrollIntoView === 'function') {
        focusTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if ('focus' in focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
      setPendingOverviewFocus(null);
    });
  }, [activeTab, pendingOverviewFocus]);

  // Poll filing status when pending/submitted
  useEffect(() => {
    if (!filingRecord || !user?.id) return;
    if (filingRecord.status !== 'pending' && filingRecord.status !== 'submitted') return;
    const id = setInterval(() => {
      if (!user?.id || !filingRecord) return;
      getFilingStatus(user.id, filingRecord.id)
        .then((res) => {
          setFilingRecord(res.filingRecord);
          setFilingHistory((prev) => [
            res.filingRecord,
            ...prev.filter((record) => record.id !== res.filingRecord.id),
          ].sort(sortFilingRecordsByRecency));
          if (res.filingRecord.status === 'accepted' || res.filingRecord.status === 'rejected') {
            clearInterval(id);
            setPollingIntervalId(null);
          }
        })
        .catch(() => { /* silent */ });
    }, 5000);
    setPollingIntervalId(id);
    return () => clearInterval(id);
  }, [filingRecord?.id, filingRecord?.status, user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (pollingIntervalId) clearInterval(pollingIntervalId); };
  }, [pollingIntervalId]);

  const handleFilingSubmit = useCallback(async () => {
    if (!user?.id) return;
    const csvReportForYear = taxReports.find(
      (report) => report.yearFrom === filingYear && report.yearTo === filingYear,
    ) ?? null;
    const csvTransactions = csvReportForYear ? buildCsvTransactionsFromReport(csvReportForYear) : [];

    setFilingLoading(true);
    setFilingStatus({
      tone: 'info',
      title: filingProvider === 'csv' ? 'Preparing CSV download' : 'Submitting filing',
      message:
        filingProvider === 'csv'
          ? `Preparing the ${filingYear} CSV export. Pinecurl will keep the last truthful filing proof mounted while this finishes.`
          : `Submitting the ${filingYear} ${formatTaxFilingProviderLabel(filingProvider)} filing request while keeping the last truthful proof mounted.`,
    });
    try {
      if (filingProvider === 'csv') {
        if (!csvReportForYear || csvTransactions.length === 0) {
          setFilingStatus({
            tone: 'error',
            title: 'No stored report for CSV completion',
            message: `Generate or reopen a ${filingYear} tax report on /transactions before Pinecurl can offer download-only CSV proof here.`,
          });
          return;
        }

        const blob = await downloadCsvFiling(user.id, {
          taxYear: filingYear,
          provider: 'csv',
          platform: 'universal',
          transactions: csvTransactions,
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const fileName = `crypto_transactions_${filingYear}.csv`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        a.remove();
        setCsvDownloadProof({
          year: filingYear,
          downloadedAt: new Date().toISOString(),
          fileName,
        });
        setFilingStatus({
          tone: 'success',
          title: 'CSV downloaded',
          message: `Downloaded ${fileName} from the stored ${filingYear} report. Pinecurl exported a file only and did not submit anything automatically.`,
        });
      } else {
        const req = filingProvider === 'hmrc'
          ? { taxYear: filingYear, provider: filingProvider as FilingProvider, hmrcToken }
          : { taxYear: filingYear, provider: filingProvider as FilingProvider };
        const res = await submitTaxFiling(user.id, req);
        setFilingRecord(res.filingRecord);
        setFilingHistory((prev) => [
          res.filingRecord,
          ...prev.filter((record) => record.id !== res.filingRecord.id),
        ].sort(sortFilingRecordsByRecency));
        setFilingStatus({
          tone:
            res.filingRecord.status === 'accepted'
              ? 'success'
              : res.filingRecord.status === 'rejected'
                ? 'error'
                : 'info',
          title:
            res.filingRecord.status === 'accepted'
              ? 'Filing accepted'
              : res.filingRecord.status === 'submitted'
                ? 'Submitted — awaiting acceptance'
                : res.filingRecord.status === 'rejected'
                  ? 'Rejected — action required'
                  : 'Filing in progress',
          message:
            res.filingRecord.status === 'accepted'
              ? `${formatTaxFilingProviderLabel(res.filingRecord.provider)} accepted the ${res.filingRecord.taxYear} filing proof on this route.`
              : res.filingRecord.status === 'submitted'
                ? `${formatTaxFilingProviderLabel(res.filingRecord.provider)} received the ${res.filingRecord.taxYear} filing, but Pinecurl will only treat accepted proof as complete.`
                : res.filingRecord.status === 'rejected'
                  ? res.filingRecord.errorMessage
                    ?? `${formatTaxFilingProviderLabel(res.filingRecord.provider)} rejected the ${res.filingRecord.taxYear} filing. Correct it on this route before retrying.`
                  : `${formatTaxFilingProviderLabel(res.filingRecord.provider)} still shows the ${res.filingRecord.taxYear} filing as pending. Pinecurl keeps the mounted proof in progress until the provider accepts it.`,
        });
      }
    } catch (e) {
      const msg =
        e instanceof TaxFilingApiError && e.errorCode === 'CSV_TRANSACTIONS_REQUIRED'
          ? `Pinecurl needs a stored ${filingYear} report with verified transaction rows before it can produce CSV download-only proof here.`
          : e instanceof Error
          ? e.message
          : 'Filing failed. Please try again.';
      setFilingStatus({
        tone: 'error',
        title:
          e instanceof TaxFilingApiError && e.errorCode === 'CSV_TRANSACTIONS_REQUIRED'
            ? 'CSV proof is blocked'
            : 'Filing request failed',
        message: msg,
      });
    } finally {
      setFilingLoading(false);
    }
  }, [user?.id, taxReports, filingProvider, filingYear, hmrcToken]);

  const filingStatusColor = (status: string) => {
    if (status === 'accepted') return 'text-emerald-400';
    if (status === 'submitted') return 'text-blue-400';
    if (status === 'rejected') return 'text-red-400';
    return 'text-yellow-400';
  };

  const fifo = useTaxLots('fifo');
  const lifo = useTaxLots('lifo');
  const hifo = useTaxLots('hifo');
  const activeLots = activeMethod === 'fifo' ? fifo : activeMethod === 'lifo' ? lifo : hifo;

  // Fetch income events for the selected year
  useEffect(() => {
    if (!user?.id) return;
    setIncomeLoading(true);
    setIncomeError(null);
    getIncomeEvents(user.id, incomeYear, incomeYear)
      .then((data) => setIncomeData(data))
      .catch((e: Error) => setIncomeError(e.message || 'Failed to load income events'))
      .finally(() => setIncomeLoading(false));
  }, [user?.id, incomeYear]);

  // Best method = lowest unrealised gain (minimises tax)
  const methodSummaries = {
    fifo: fifo.data?.summary.totalUnrealisedGainLoss ?? null,
    lifo: lifo.data?.summary.totalUnrealisedGainLoss ?? null,
    hifo: hifo.data?.summary.totalUnrealisedGainLoss ?? null,
  };

  const referenceGain = methodSummaries.fifo;
  const hifoGain = methodSummaries.hifo;
  const potentialSaving =
    referenceGain !== null && hifoGain !== null ? referenceGain - hifoGain : null;

  const isHarvestingAvailable = Boolean(user?.id);
  useEffect(() => {
    if (!user?.id) return;
    setTaxReportLoading(true);
    setTaxReportError(null);
    getTaxReports(user.id, 25, 0)
      .then((res) => {
        const sortedReports = [...res.reports].sort(sortTaxReportsByRecency);
        setTaxReports(sortedReports);
        setTaxReport(sortedReports[0] ?? null);
      })
      .catch(() => {
        setTaxReports([]);
        setTaxReport(null);
        setTaxReportError('Unable to load stored tax reports');
      })
      .finally(() => setTaxReportLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    setOverviewHarvestSummaryLoading(true);
    setOverviewHarvestSummaryError(null);
    getHarvestingSummary(user.id)
      .then((summary) => {
        setOverviewHarvestSummary(summary);
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to load harvesting proof right now.';
        setOverviewHarvestSummaryError(message);
      })
      .finally(() => setOverviewHarvestSummaryLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !isHarvestingAvailable) {
      return;
    }

    getHarvestingOpportunities(user.id, 100)
      .then((payload) => {
        setOverviewHarvestOpportunities(payload);
      })
      .catch(() => {
        setOverviewHarvestOpportunities(null);
      });
  }, [isHarvestingAvailable, user?.id]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    setOverviewExportSummaryLoading(true);
    setOverviewExportSummaryError(null);
    getTaxExportSummary(user.id, selectedOverviewExportYear)
      .then((summary) => {
        setOverviewExportSummary(summary);
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to load export proof right now.';
        setOverviewExportSummaryError(message);
      })
      .finally(() => setOverviewExportSummaryLoading(false));
  }, [selectedOverviewExportYear, user?.id]);

  useEffect(() => {
    if (!taxHandoff?.method || !taxHandoff?.jurisdiction) {
      return;
    }

    setActiveMethod((currentMethod) =>
      currentMethod === taxHandoff.method ? currentMethod : taxHandoff.method,
    );
    setJurisdiction((currentJurisdiction) =>
      currentJurisdiction === taxHandoff.jurisdiction
        ? currentJurisdiction
        : taxHandoff.jurisdiction,
    );
  }, [taxHandoff?.jurisdiction, taxHandoff?.method]);

  useEffect(() => {
    if (activeTab !== 'overview' || taxHandoff?.step !== 'review-lots') {
      return;
    }

    const focusTarget =
      taxHandoff.focus === 'method-comparison'
        ? reviewMethodComparisonRef.current
        : reviewLotLedgerRef.current;

    if (!focusTarget) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (typeof focusTarget.scrollIntoView === 'function') {
        focusTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      focusTarget.focus();
    });
  }, [activeTab, taxHandoff?.focus, taxHandoff?.step]);

  const activeLotCount = activeLots.data?.summary.lotCount ?? 0;
  const activeLotTotal = activeLots.data?.lots.length ?? 0;
  const noTaxLotsMounted = !activeLots.isLoading && !activeLots.error && activeLotTotal === 0;
  const methodOptions = useMemo(() => (
    METHODS
      .map((method) => ({
        method: method.value,
        gain: methodSummaries[method.value],
      }))
      .filter((option): option is { method: CostBasisMethod; gain: number } => option.gain !== null)
      .sort((left, right) => left.gain - right.gain)
  ), [methodSummaries]);
  const bestMethod = methodOptions[0]?.method ?? null;
  const bestMethodGain = bestMethod ? methodSummaries[bestMethod] : null;
  const activeMethodGain = methodSummaries[activeMethod];
  const methodImprovement =
    activeMethodGain !== null && bestMethodGain !== null
      ? activeMethodGain - bestMethodGain
      : null;
  const shouldReviewMethodChoice =
    Boolean(bestMethod)
    && bestMethod !== activeMethod
    && methodImprovement !== null
    && methodImprovement > 0;
  const overviewHarvestOpportunityCount =
    overviewHarvestOpportunities?.count
    ?? overviewHarvestSummary?.opportunityCount
    ?? 0;
  const overviewHarvestProofValue =
    overviewHarvestSummary?.displayValues?.totalHarvestableLoss != null
      ? formatCurrency(Math.abs(overviewHarvestSummary.displayValues.totalHarvestableLoss))
      : overviewHarvestSummary
      ? `${formatUsdValue(Math.abs(overviewHarvestSummary.totalHarvestableLossUsd))} raw USD`
      : undefined;
  const activeOverviewExportSummary =
    overviewExportSummary?.year === selectedOverviewExportYear
      ? overviewExportSummary
      : null;
  const overviewExportDisposalCount = activeOverviewExportSummary?.disposalCount ?? 0;
  const overviewRailLoading =
    (!fifo.data && fifo.isLoading)
    || (!lifo.data && lifo.isLoading)
    || (!hifo.data && hifo.isLoading)
    || (!overviewHarvestSummary && overviewHarvestSummaryLoading)
    || (!activeOverviewExportSummary && overviewExportSummaryLoading);

  const dominantTaxRail = useMemo<DominantTaxRail>(() => {
    if (fifo.error || lifo.error || hifo.error) {
      return {
        step: 'review-lots',
        tab: 'overview',
        focus: 'lot-ledger',
        title: 'Review lot truth before Pinecurl ranks the next tax action',
        message: 'Lot data is locally degraded, so Pinecurl is keeping harvest and export secondary until the lot ledger is trustworthy again.',
        ctaLabel: 'Review lot ledger',
        proofLabel: fifo.error ?? lifo.error ?? hifo.error ?? 'Tax lot proof is unavailable.',
        proofCount: activeLotCount || undefined,
        secondaryActions: [
          { key: 'harvesting', label: 'View harvesting', tab: 'harvesting' },
          { key: 'export', label: 'Open export proof', tab: 'export' },
        ],
      };
    }

    if (activeLotTotal === 0) {
      return {
        step: 'review-lots',
        tab: 'overview',
        focus: 'lot-ledger',
        title: 'Review lots before Pinecurl suggests harvest or export',
        message: 'No lots are mounted yet, so the honest next step is to confirm cost-basis inputs before any harvest or filing follow-up.',
        ctaLabel: 'Review lot ledger',
        proofLabel: 'No tax lots are available on the current overview.',
        secondaryActions: [
          { key: 'harvesting', label: 'View harvesting', tab: 'harvesting' },
          { key: 'export', label: 'Open export proof', tab: 'export' },
        ],
      };
    }

    if (shouldReviewMethodChoice && bestMethod && methodImprovement !== null) {
      return {
        step: 'review-lots',
        tab: 'overview',
        focus: 'method-comparison',
        title: 'Review cost-basis method before harvesting or exporting',
        message: `${formatMethodLabel(bestMethod)} currently shows the lowest unrealised gain. Confirm that method choice before acting on harvest or filing steps.`,
        ctaLabel: 'Review method comparison',
        proofLabel: `${formatMethodLabel(bestMethod)} could reduce unrealised gains by ${formatCurrency(methodImprovement)} versus ${formatMethodLabel(activeMethod)}.`,
        proofCount: activeLotCount,
        proofValue: formatCurrency(methodImprovement),
        secondaryActions: [
          { key: 'harvesting', label: 'View harvesting', tab: 'harvesting' },
          { key: 'export', label: 'Open export proof', tab: 'export' },
        ],
      };
    }

    if (overviewHarvestOpportunityCount > 0 && overviewHarvestSummary) {
      return {
        step: 'harvest',
        tab: 'harvesting',
        focus: 'harvest-summary',
        title: 'Harvest losses before moving to filing exports',
        message: 'Harvest proof is stronger than export readiness right now, so Pinecurl is keeping the next action on the existing harvesting tab.',
        ctaLabel: 'Review harvesting',
        proofLabel: `${overviewHarvestOpportunityCount} harvest opportunity${overviewHarvestOpportunityCount === 1 ? '' : 'ies'} are ready for review.`,
        proofCount: overviewHarvestOpportunityCount,
        proofValue: overviewHarvestProofValue,
        year: taxReport?.yearTo ?? selectedOverviewExportYear,
        secondaryActions: [
          { key: 'export', label: 'Open export proof', tab: 'export' },
          { key: 'simulator', label: 'Open simulator', tab: 'simulator' },
        ],
      };
    }

    if (activeOverviewExportSummary?.hasDisposals) {
      return {
        step: 'export',
        tab: 'export',
        focus: 'export-proof',
        title: `Export the ${selectedOverviewExportYear} filing dataset next`,
        message: 'Review-lot blockers are clear and harvesting is not stronger, so Pinecurl can keep the next action on the existing export tab.',
        ctaLabel: 'Open export proof',
        proofLabel: `${overviewExportDisposalCount} realised disposal${overviewExportDisposalCount === 1 ? '' : 's'} are ready for export in ${selectedOverviewExportYear}.`,
        proofCount: overviewExportDisposalCount,
        year: selectedOverviewExportYear,
        secondaryActions: [
          { key: 'harvesting', label: 'View harvesting', tab: 'harvesting' },
          { key: 'simulator', label: 'Open simulator', tab: 'simulator' },
        ],
      };
    }

    return {
      step: 'review-lots',
      tab: 'overview',
      focus: 'lot-ledger',
      title: 'Review lots before Pinecurl recommends a filing step',
      message: 'Harvest and export proof are still local follow-ups, so Pinecurl is keeping the dominant action on the lot review sections first.',
      ctaLabel: 'Review lot ledger',
      proofLabel: taxReport
        ? `${activeLotCount} lots are loaded, but there is no stronger harvest/export proof than the current overview lot context.`
        : `${activeLotCount} lots are loaded and Pinecurl still needs lot review before it can elevate harvest or export.`,
      proofCount: activeLotCount,
      secondaryActions: [
        { key: 'harvesting', label: 'View harvesting', tab: 'harvesting' },
        { key: 'export', label: 'Open export proof', tab: 'export' },
      ],
    };
  }, [
    activeLotCount,
    activeLotTotal,
    activeMethod,
    bestMethod,
    fifo.data,
    fifo.error,
    lifo.data,
    lifo.error,
    methodImprovement,
    overviewExportDisposalCount,
    activeOverviewExportSummary?.hasDisposals,
    overviewHarvestOpportunityCount,
    overviewHarvestProofValue,
    overviewHarvestSummary,
    selectedOverviewExportYear,
    shouldReviewMethodChoice,
    taxReport,
    hifo.data,
    hifo.error,
    formatCurrency,
  ]);
  const overviewProofLabel =
    dominantTaxRail.step === 'harvest'
      ? 'Harvest opportunity proof'
      : dominantTaxRail.step === 'export'
        ? `${selectedOverviewExportYear} tax export proof`
        : 'Tax lot proof';
  const overviewProofTime = formatSourceProofOccurredAt(
    dominantTaxRail.step === 'export'
      ? activeOverviewExportSummary?.datasetStatus?.updatedAt
        ?? taxReport?.generatedAt
        ?? null
      : taxReport?.generatedAt ?? null,
  );
  const overviewProofHelper =
    overviewHarvestSummaryError || overviewExportSummaryError
      ? {
          tone: 'warning' as const,
          title: 'Secondary tax proof is still rechecking',
          message: [
            overviewHarvestSummaryError ? `Harvesting: ${overviewHarvestSummaryError}` : null,
            overviewExportSummaryError ? `Export: ${overviewExportSummaryError}` : null,
          ].filter(Boolean).join(' · '),
        }
      : activeLotTotal === 0
        ? {
            tone: 'warning' as const,
            title: 'No lot proof yet',
            message: 'Import or sync transactions first so Pinecurl can mount lot, harvest, and export proof on this route.',
          }
        : {
            tone: 'info' as const,
            title: 'Mounted route proof',
            message: dominantTaxRail.proofLabel,
          };

  const activeDisplayCurrency = user?.displayCurrency ?? user?.preferredCurrency ?? currency;
  const exportSelectedYear = exportPanelSnapshot?.selectedYear ?? selectedOverviewExportYear;
  const exportSelectedCurrency = exportPanelSnapshot?.selectedCurrency ?? activeDisplayCurrency;
  const activeRailExportSummary =
    exportPanelSnapshot?.summary?.year === exportSelectedYear
      ? exportPanelSnapshot.summary
      : activeOverviewExportSummary?.year === exportSelectedYear
      ? activeOverviewExportSummary
      : null;
  const filingRecords = useMemo(() => {
    const deduped = new Map<string, TaxFilingRecord>();
    [filingRecord, ...filingHistory].forEach((record) => {
      if (!record || deduped.has(record.id)) {
        return;
      }
      deduped.set(record.id, record);
    });
    return Array.from(deduped.values()).sort(sortFilingRecordsByRecency);
  }, [filingHistory, filingRecord]);
  const activeFilingEvidence = useMemo(
    () =>
      filingRecords.find(
        (record) => record.provider === filingProvider && record.taxYear === filingYear,
      ) ?? null,
    [filingProvider, filingRecords, filingYear],
  );
  const readinessYear = displayedFilingReadiness?.year ?? filingYear;
  const readinessProvider = displayedFilingReadiness?.provider ?? filingProvider;
  const querySourceProof = useMemo(
    () => readTaxSourceProofFromSearchParams(searchParams, {
      year: readinessYear,
      provider: readinessProvider,
    }),
    [readinessProvider, readinessYear, searchParams],
  );
  const carriedSourceProof = useMemo<TaxSourceProofSnapshot | null>(() => {
    const proof = querySourceProof ?? persistedSourceProof;
    if (!proof) {
      return null;
    }

    const resolvedYear = proof.year ?? readinessYear;
    const resolvedProvider = proof.provider ?? readinessProvider;

    if (proof.year === resolvedYear && proof.provider === resolvedProvider) {
      return proof;
    }

    return {
      ...proof,
      year: resolvedYear,
      provider: resolvedProvider,
    };
  }, [persistedSourceProof, querySourceProof, readinessProvider, readinessYear]);
  const carriedSourceFreshness = useMemo(
    () => buildSourceFreshnessFromTaxSourceProof(carriedSourceProof),
    [carriedSourceProof],
  );
  const sourceTruthRequired = !carriedSourceProof;
  const sourceTruthRequiredLabel = `Review transactions first for ${readinessYear}`;
  const sourceTruthRequiredMessage =
    'Review /transactions first so Pinecurl can carry mounted ledger or import proof into this decision owner before it promotes export or filing.';

  useEffect(() => {
    if (!user?.id || !carriedSourceProof) {
      return;
    }

    persistTaxSourceProof(user.id, carriedSourceProof);
    if (
      !persistedSourceProof
      || persistedSourceProof.sourceLabel !== carriedSourceProof.sourceLabel
      || persistedSourceProof.importedAt !== carriedSourceProof.importedAt
      || persistedSourceProof.importedCount !== carriedSourceProof.importedCount
      || persistedSourceProof.skippedCount !== carriedSourceProof.skippedCount
      || persistedSourceProof.errorCount !== carriedSourceProof.errorCount
      || persistedSourceProof.matchedCount !== carriedSourceProof.matchedCount
      || persistedSourceProof.year !== carriedSourceProof.year
      || persistedSourceProof.provider !== carriedSourceProof.provider
    ) {
      setPersistedSourceProof(carriedSourceProof);
    }
  }, [carriedSourceProof, persistedSourceProof, user?.id]);

  const relatedHistoryEvidence = useMemo(() => {
    const relevantYears = new Set<number>([filingYear, exportSelectedYear, readinessYear]);
    return filingRecords.filter((record) => relevantYears.has(record.taxYear)).slice(0, 3);
  }, [exportSelectedYear, filingRecords, filingYear, readinessYear]);
  const latestReadyExportAction = useMemo(
    () => getLatestExportAction(exportPanelSnapshot, ['ready']),
    [exportPanelSnapshot],
  );
  const yearsDiverged = exportSelectedYear !== filingYear;
  const activeCsvDownloadProof =
    filingProvider === 'csv' && csvDownloadProof?.year === filingYear ? csvDownloadProof : null;
  const matchingStoredCsvReport = useMemo(
    () => taxReports.find((report) => report.yearFrom === filingYear && report.yearTo === filingYear) ?? null,
    [filingYear, taxReports],
  );
  const matchingStoredCsvTransactions = useMemo(
    () => matchingStoredCsvReport ? buildCsvTransactionsFromReport(matchingStoredCsvReport) : [],
    [matchingStoredCsvReport],
  );
  const hasMountedExportProofForFilingYear =
    activeRailExportSummary?.hasDisposals && exportSelectedYear === filingYear;
  const csvStoredReportReady = Boolean(
    matchingStoredCsvReport && matchingStoredCsvTransactions.length > 0,
  );
  const filingActionBlocked =
    (filingProvider === 'csv' && !csvStoredReportReady)
    || ((filingProvider === 'turbotax' || filingProvider === 'hmrc')
      && !activeFilingEvidence
      && !hasMountedExportProofForFilingYear);
  const filingActionBlockedReason =
    filingProvider === 'csv' && !csvStoredReportReady
      ? matchingStoredCsvReport
        ? 'Download CSV unlocks after Pinecurl mounts realised transaction rows for the selected stored report.'
        : `Download CSV unlocks after Pinecurl mounts a stored ${filingYear} report for this year.`
      : filingProvider === 'hmrc' && !hmrcToken
        ? 'Add an HMRC OAuth2 access token to enable the submit button.'
        : (filingProvider === 'turbotax' || filingProvider === 'hmrc') && !activeFilingEvidence && !hasMountedExportProofForFilingYear
          ? `Review export proof for ${filingYear} before submitting to ${formatTaxFilingProviderLabel(filingProvider)}.`
          : null;
  const filingProviderSelectionHint =
    filingProvider === 'csv'
      ? 'CSV selection only chooses download-only mode. Use the Download CSV button below once stored report proof is mounted.'
      : filingProvider === 'hmrc'
        ? 'HMRC selection only chooses the submission route. Add a token, then use the submit button below when export proof is ready.'
        : 'TurboTax selection only chooses the submission route. Use the submit button below when export proof is mounted for this filing year.';
  const filingTruthStage = displayedFilingReadiness
    ? getTaxFilingTruthStage(displayedFilingReadiness)
    : activeFilingEvidence?.status ?? 'not_filed';
  const filingReadinessBlockedByMissingLots =
    displayedFilingReadiness?.verdict === 'blocked'
    && displayedFilingReadiness.blockerCode === 'missing_lots';
  const filingReadinessBlockedByStoredReport =
    displayedFilingReadiness?.verdict === 'blocked'
    && displayedFilingReadiness.blockerCode === 'no_stored_report';
  const exportProofOccurredAt = formatSourceProofOccurredAt(
    activeCsvDownloadProof?.downloadedAt
    ?? latestReadyExportAction?.completedAt
    ?? latestReadyExportAction?.updatedAt
    ?? displayedFilingReadiness?.proof.export.completedAt
    ?? displayedFilingReadiness?.proof.export.updatedAt
    ?? activeRailExportSummary?.datasetStatus?.updatedAt
    ?? null,
  );
  const completionOwnerMode =
    filingTruthStage === 'accepted'
      ? 'review-filed-history'
      : filingTruthStage === 'rejected'
        ? 'retry-filing'
        : filingTruthStage === 'pending' || filingTruthStage === 'submitted'
          ? 'filing-status'
          : filingReadinessBlockedByStoredReport
            ? 'prepare-report'
            : filingReadinessBlockedByMissingLots
              ? 'return-to-harvest'
              : 'export-proof';
  const filingSurfaceOwnsPrimaryCTA =
    completionOwnerMode === 'review-filed-history'
    || completionOwnerMode === 'retry-filing'
    || completionOwnerMode === 'filing-status';
  const overviewRailOwnsPrimaryCTA = !filingSurfaceOwnsPrimaryCTA;
  const completionProofTime = formatSourceProofOccurredAt(
    getFilingReadinessProofAt(displayedFilingReadiness, activeCsvDownloadProof),
  );
  const completionSurfaceProofTime =
    completionOwnerMode === 'export-proof'
      ? exportProofOccurredAt ?? completionProofTime
      : completionProofTime ?? exportProofOccurredAt;
  const completionProofLabel =
    sourceTruthRequired
      ? sourceTruthRequiredLabel
      : completionOwnerMode === 'export-proof'
      ? getTaxFilingOwnerHeadline(displayedFilingReadiness ?? {
          year: exportSelectedYear,
          provider: readinessProvider,
          verdict: activeRailExportSummary?.hasDisposals ? 'ready' : 'blocked',
          blockerCode: activeRailExportSummary?.hasDisposals ? null : 'no_export_proof',
          proofSummary: activeRailExportSummary?.hasDisposals
            ? `Ready to export for ${exportSelectedYear}.`
            : `Missing export proof for ${exportSelectedYear}.`,
          dominantAction: {
            label: 'review export proof',
          },
          proof: {
            lots: {
              available: false,
              blockedByMissingLots: false,
              lotCount: 0,
              disposalCount: 0,
              assignedDisposalCount: 0,
              affectedTransactionCount: null,
              updatedAt: null,
            },
            report: {
              available: false,
              reportId: null,
              method: null,
              generatedAt: null,
              transactionCount: null,
              reusableRowCount: 0,
            },
            export: {
              available: Boolean(activeRailExportSummary?.hasDisposals),
              state: activeRailExportSummary?.hasDisposals ? 'ready' : null,
              format: null,
              updatedAt: activeRailExportSummary?.datasetStatus?.updatedAt ?? null,
              completedAt: activeRailExportSummary?.datasetStatus?.completedAt ?? null,
              fileName: latestReadyExportAction?.fileName ?? null,
              disposalCount: activeRailExportSummary?.disposalCount ?? null,
            },
            filing: {
              available: false,
              id: null,
              status: null,
              provider: null,
              submittedAt: null,
              confirmationNumber: null,
              updatedAt: null,
            },
          },
        } as TaxFilingReadinessPayload)
      : displayedFilingReadiness
        ? getFilingReadinessHeadline(displayedFilingReadiness)
        : filingReadinessLoading
          ? 'Loading filing readiness'
          : filingReadinessError
            ? 'Filing readiness unavailable'
            : 'Loading filing readiness';

  const completionChips = [
    { label: 'Filing year', value: String(readinessYear) },
    ...(yearsDiverged ? [{ label: 'Export year', value: String(exportSelectedYear) }] : []),
    { label: 'Provider', value: formatTaxFilingProviderLabel(readinessProvider) },
    { label: 'Status', value: displayedFilingReadiness ? getTaxFilingTruthChipLabel(displayedFilingReadiness) : 'Not filed' },
    { label: 'Jurisdiction', value: jurisdiction },
    { label: 'Method', value: activeMethod.toUpperCase() },
    { label: 'Currency', value: exportSelectedCurrency },
  ];

  const completionExportStatus = displayedFilingReadiness
    ? sourceTruthRequired
      ? {
        tone: 'warning' as const,
        title: 'Review transactions first',
        message: 'Mount source truth on /transactions before you rely on export proof on this decision-owner route.',
        meta: `Filing year ${readinessYear}`,
      }
      :
      activeCsvDownloadProof
        ? {
          tone: 'success' as const,
          title: 'Download proof mounted',
          message: `Downloaded ${activeCsvDownloadProof.fileName} for ${displayedFilingReadiness.year}.`,
          meta: `Downloaded ${new Date(activeCsvDownloadProof.downloadedAt).toLocaleDateString()} · Provider ${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)}`,
        }
        : displayedFilingReadiness.proof.export.available
          ? {
            tone: 'success' as const,
            title: 'Export proof mounted',
            message: displayedFilingReadiness.proof.export.fileName
              ? `${displayedFilingReadiness.proof.export.fileName} is the mounted export proof for ${displayedFilingReadiness.year}.`
              : `${displayedFilingReadiness.proof.export.disposalCount ?? 0} realised disposal${displayedFilingReadiness.proof.export.disposalCount === 1 ? '' : 's'} are ready for ${displayedFilingReadiness.year}.`,
            meta: [
              displayedFilingReadiness.proof.export.format ? `Format ${displayedFilingReadiness.proof.export.format}` : null,
              displayedFilingReadiness.proof.export.state ? `State ${displayedFilingReadiness.proof.export.state}` : null,
              displayedFilingReadiness.proof.export.completedAt
                ? `Completed ${new Date(displayedFilingReadiness.proof.export.completedAt).toLocaleDateString()}`
                : null,
            ].filter(Boolean).join(' · '),
          }
          : displayedFilingReadiness.blockerCode === 'no_export_proof' || displayedFilingReadiness.blockerCode === 'no_stored_report'
            ? {
              tone: 'warning' as const,
              title: 'Missing export proof',
              message: displayedFilingReadiness.blockerCode === 'no_stored_report'
                ? `No stored report has mounted reusable export rows for ${displayedFilingReadiness.year} yet.`
                : `No mounted export proof is available for ${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)} ${displayedFilingReadiness.year}.`,
              meta: displayedFilingReadiness.proof.export.state
                ? `Latest export state ${displayedFilingReadiness.proof.export.state}`
                : `Filing year ${displayedFilingReadiness.year}`,
            }
            : displayedFilingReadiness.proof.report.available
              ? {
                tone: 'info' as const,
                title: 'Stored report mounted',
                message: `${displayedFilingReadiness.proof.report.method ?? 'Stored'} report proof is mounted with ${displayedFilingReadiness.proof.report.reusableRowCount} reusable row${displayedFilingReadiness.proof.report.reusableRowCount === 1 ? '' : 's'}.`,
                meta: `Report year ${displayedFilingReadiness.year}`,
              }
              : {
                tone: displayedFilingReadiness.blockerCode === 'missing_lots' ? 'warning' as const : 'neutral' as const,
                title: displayedFilingReadiness.blockerCode === 'missing_lots' ? 'Missing lots' : 'Export proof is local',
                message: displayedFilingReadiness.blockerCode === 'missing_lots'
                  ? `${displayedFilingReadiness.proof.lots.affectedTransactionCount ?? 0} disposal transaction${displayedFilingReadiness.proof.lots.affectedTransactionCount === 1 ? '' : 's'} still need lot coverage before export proof can mount.`
                  : displayedFilingReadiness.proofSummary,
                meta: `Filing year ${displayedFilingReadiness.year}`,
              }
    : {
      tone: filingReadinessLoading ? 'info' as const : filingReadinessError ? 'warning' as const : 'neutral' as const,
      title: filingReadinessLoading ? 'Rechecking readiness proof' : 'Readiness proof unavailable',
      message: filingReadinessError ?? 'Pinecurl is loading the selected filing-year proof on this route.',
      meta: `Provider ${formatTaxFilingProviderLabel(filingProvider)} · Filing year ${filingYear}`,
    };

  const completionFilingStatus = sourceTruthRequired
    ? {
      tone: 'neutral' as const,
      title: 'Decision owner is waiting on source truth',
      message: 'This route keeps filing actions local, but it will not promote a provider decision until /transactions remounts the carried source proof.',
      meta: `Provider ${formatTaxFilingProviderLabel(readinessProvider)} · Filing year ${readinessYear}`,
    }
    : displayedFilingReadiness?.proof.filing.available
    ? {
      tone:
        filingTruthStage === 'accepted'
          ? 'success' as const
          : filingTruthStage === 'rejected'
            ? 'error' as const
          : 'info' as const,
      title:
        filingTruthStage === 'accepted'
          ? 'Accepted by provider'
          : filingTruthStage === 'submitted'
            ? 'Submitted — awaiting acceptance'
            : filingTruthStage === 'rejected'
              ? 'Rejected — action required'
              : 'Pending with provider',
      message:
        filingTruthStage === 'accepted'
          ? `${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)} accepted the ${displayedFilingReadiness.year} filing proof.`
          : filingTruthStage === 'submitted'
            ? `${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)} received the ${displayedFilingReadiness.year} filing, but only accepted proof counts as complete.`
            : filingTruthStage === 'rejected'
              ? activeFilingEvidence?.errorMessage
                ?? `The latest ${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)} filing attempt was rejected. Correct it on this route before retrying.`
              : `${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)} still shows the ${displayedFilingReadiness.year} filing as pending. Pinecurl keeps this proof in progress until the provider accepts it.`,
      meta: [
        displayedFilingReadiness.proof.filing.confirmationNumber
          ? `Confirmation ${displayedFilingReadiness.proof.filing.confirmationNumber}`
          : 'No confirmation number yet',
        displayedFilingReadiness.proof.filing.submittedAt
          ? `Submitted ${new Date(displayedFilingReadiness.proof.filing.submittedAt).toLocaleDateString()}`
          : displayedFilingReadiness.proof.filing.updatedAt
            ? `Updated ${new Date(displayedFilingReadiness.proof.filing.updatedAt).toLocaleDateString()}`
            : `Filing year ${displayedFilingReadiness.year}`,
      ].join(' · '),
    }
    : activeCsvDownloadProof
      ? {
        tone: 'success' as const,
        title: 'CSV downloaded only',
        message: `Downloaded ${activeCsvDownloadProof.fileName}. Pinecurl exported a file and did not submit anything automatically.`,
        meta: `Downloaded ${new Date(activeCsvDownloadProof.downloadedAt).toLocaleDateString()} · Filing year ${filingYear}`,
      }
      : filingStatus
        ? {
          tone: filingStatus.tone,
          title: filingStatus.title,
          message: filingStatus.message,
          meta: `Provider ${formatTaxFilingProviderLabel(filingProvider)} · Filing year ${filingYear}`,
        }
        : displayedFilingReadiness
          ? {
            tone: filingTruthStage === 'accepted' ? 'success' as const : 'neutral' as const,
            title: filingTruthStage === 'accepted' ? 'Already filed' : 'No filing proof yet',
            message: filingTruthStage === 'accepted'
              ? `A truthful accepted filing record is mounted for ${displayedFilingReadiness.year}.`
              : `No filing record is mounted yet for ${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)} ${displayedFilingReadiness.year}.`,
            meta: `Provider ${formatTaxFilingProviderLabel(readinessProvider)} · Filing year ${readinessYear}`,
          }
          : {
            tone: filingLoading ? 'info' as const : 'neutral' as const,
            title: filingLoading ? 'Submitting filing' : 'No filing proof yet',
            message: filingLoading
              ? `The ${formatTaxFilingProviderLabel(filingProvider)} filing request is still running locally.`
              : `No filing record is mounted yet for ${formatTaxFilingProviderLabel(filingProvider)} ${filingYear}.`,
            meta: `Provider ${formatTaxFilingProviderLabel(filingProvider)} · Filing year ${filingYear}`,
          };

  const dominantCompletionLabel:
    | TaxFilingReadinessActionLabel
    | 'retry filing'
    | 'review filing section'
    | 'review transactions' =
      sourceTruthRequired
        ? 'review transactions'
        : completionOwnerMode === 'review-filed-history'
        ? 'review filed history'
        : completionOwnerMode === 'retry-filing'
          ? 'retry filing'
          : completionOwnerMode === 'filing-status'
            ? 'review filing section'
            : completionOwnerMode === 'prepare-report'
              ? 'prepare report on /transactions'
              : completionOwnerMode === 'return-to-harvest'
                ? 'return to harvest/lots'
                : 'review export proof';
  const completionHelperStatus = displayedFilingReadiness
    ? sourceTruthRequired
      ? {
        tone: 'warning' as const,
        title: 'Review transactions first',
        message: 'The carried source proof is not mounted on this route. Return to /transactions first, then come back here for the canonical decision verdict.',
        meta: 'Owner route /transactions',
      }
      : filingReadinessError
      ? {
        tone: 'warning' as const,
        title: 'Rechecking the same year/provider proof',
        message: 'Pinecurl kept the last truthful readiness verdict mounted while this route retried the same year/provider proof.',
        meta: `Route state: ${filingReadinessError}`,
      }
      : filingReadinessRechecking
        ? {
          tone: 'info' as const,
          title: 'Rechecking the same year/provider proof',
          message: 'Pinecurl is rechecking the same year/provider proof on this route while keeping the mounted readiness verdict visible.',
          meta: `Provider ${formatTaxFilingProviderLabel(readinessProvider)} · Filing year ${readinessYear}`,
        }
        : filingReadinessRecovered
          ? {
            tone: 'info' as const,
            title: 'Recovered',
            message: 'Recovered on this route. Pinecurl revalidated the same year/provider proof and kept the truthful readiness verdict mounted.',
            meta: `Provider ${formatTaxFilingProviderLabel(readinessProvider)} · Filing year ${readinessYear}`,
          }
          : displayedFilingReadiness.verdict === 'blocked'
            ? {
              tone: 'warning' as const,
              title: 'Blocked',
              message:
                displayedFilingReadiness.blockerCode === 'missing_lots'
                  ? `missing lots are still blocking filing readiness for ${displayedFilingReadiness.year}. Return to the existing harvest/lots flow, then come back here for the canonical verdict.`
                  : displayedFilingReadiness.blockerCode === 'no_stored_report'
                  ? `missing export proof for ${displayedFilingReadiness.year} still depends on a stored report from /transactions. Prepare that report, then return here for the canonical readiness proof.`
                    : displayedFilingReadiness.blockerCode === 'no_export_proof'
                    ? `missing export proof is still blocking ${formatTaxFilingProviderLabel(displayedFilingReadiness.provider)} ${displayedFilingReadiness.year}. Review export proof on this route to finish the owner flow.`
                      : displayedFilingReadiness.proofSummary,
              meta: displayedFilingReadiness.proofSummary,
            }
            : null
    : filingReadinessEmpty
      ? {
        tone: 'info' as const,
        title: 'Readiness proof is still local',
        message: 'Pinecurl has not mounted filing readiness proof on this route yet.',
        meta: `Provider ${formatTaxFilingProviderLabel(filingProvider)} · Filing year ${filingYear}`,
      }
      : null;

  const navigateToTaxStep = useCallback((rail: DominantTaxRail) => {
    const context: TaxNextStepHandoffContext = {
      handoff: 'tax-next-step',
      source: 'tax-overview',
      step: rail.step,
      tab: rail.tab,
      focus: rail.focus,
      year: rail.year,
      method: activeMethod,
      currency: activeDisplayCurrency,
      jurisdiction,
      proofLabel: rail.proofLabel,
      proofCount: rail.proofCount,
      proofValue: rail.proofValue,
    };
    const [, query = ''] = getTaxNextStepPath(context).split('?');
    setSearchParams(new URLSearchParams(query), { replace: false });
  }, [activeDisplayCurrency, activeMethod, jurisdiction, setSearchParams]);

  const openExportCompletion = useCallback(() => {
    const proofLabel = activeRailExportSummary?.hasDisposals
      ? `${activeRailExportSummary.disposalCount} realised disposal${activeRailExportSummary.disposalCount === 1 ? '' : 's'} are ready for export in ${exportSelectedYear}.`
      : `No realised disposals are available for ${exportSelectedYear}.`;
    const context: TaxNextStepHandoffContext = {
      handoff: 'tax-next-step',
      source: 'tax-overview',
      step: 'export',
      tab: 'export',
      focus: 'export-proof',
      year: exportSelectedYear,
      method: activeMethod,
      currency: exportSelectedCurrency,
      jurisdiction,
      proofLabel,
      proofCount: activeRailExportSummary?.disposalCount,
      proofValue: latestReadyExportAction?.fileName,
    };
    const [, query = ''] = getTaxNextStepPath(context).split('?');
    setSearchParams(new URLSearchParams(query), { replace: false });
  }, [
    activeMethod,
    activeRailExportSummary?.disposalCount,
    activeRailExportSummary?.hasDisposals,
    exportSelectedCurrency,
    exportSelectedYear,
    jurisdiction,
    latestReadyExportAction,
    setSearchParams,
  ]);

  const openOverviewFilingSection = useCallback((focus: 'filing-form' | 'filing-history') => {
    setPendingOverviewFocus(focus);
    setActiveTab('overview');
  }, []);

  const returnToHarvestOrLots = useCallback(() => {
    if (overviewHarvestOpportunityCount > 0 && overviewHarvestSummary) {
      navigateToTaxStep({
        step: 'harvest',
        tab: 'harvesting',
        focus: 'harvest-summary',
        title: 'Harvest losses before moving to filing exports',
        message: 'Harvest proof remains the truthful next action for this selected year.',
        ctaLabel: 'Review harvesting',
        proofLabel: `${overviewHarvestOpportunityCount} harvest opportunity${overviewHarvestOpportunityCount === 1 ? '' : 'ies'} are ready for review.`,
        proofCount: overviewHarvestOpportunityCount,
        proofValue: overviewHarvestProofValue,
        year: exportSelectedYear,
        secondaryActions: [],
      });
      return;
    }

    navigateToTaxStep({
      step: 'review-lots',
      tab: 'overview',
      focus: 'lot-ledger',
      title: 'Review lots before filing',
      message: 'Lot proof is the truthful next stop while export remains empty.',
      ctaLabel: 'Review lot ledger',
      proofLabel: `No realised disposals are available for ${exportSelectedYear}. Return to lots before exporting or filing.`,
      proofCount: activeLotCount || undefined,
      year: exportSelectedYear,
      secondaryActions: [],
    });
  }, [
    activeLotCount,
    exportSelectedYear,
    navigateToTaxStep,
    overviewHarvestOpportunityCount,
    overviewHarvestProofValue,
    overviewHarvestSummary,
  ]);

  const completionSecondaryActions = [
    {
      key: 'open-export',
      label: 'Open export proof',
      onClick: openExportCompletion,
    },
    {
      key: 'open-filing',
      label: 'Open filing section',
      onClick: () => openOverviewFilingSection('filing-form'),
    },
    {
      key: 'open-history',
      label: 'Review history',
      onClick: () => openOverviewFilingSection('filing-history'),
    },
  ].filter((action) => {
    if (dominantCompletionLabel === 'review export proof' && action.key === 'open-export') return false;
    if (dominantCompletionLabel === 'review filed history' && action.key === 'open-history') return false;
    if (dominantCompletionLabel === 'review filing section' && action.key === 'open-filing') return false;
    if (dominantCompletionLabel === 'retry filing' && action.key === 'open-filing') return false;
    return true;
  });

  const completionDominantAction = {
    label: dominantCompletionLabel,
    onClick:
      dominantCompletionLabel === 'review transactions'
        ? () => navigate('/transactions?view=taxReports')
        : dominantCompletionLabel === 'review export proof'
        ? openExportCompletion
        : dominantCompletionLabel === 'prepare report on /transactions'
          ? () => navigate('/transactions?view=taxReports')
          : dominantCompletionLabel === 'retry filing'
            ? () => openOverviewFilingSection('filing-form')
            : dominantCompletionLabel === 'review filed history'
              ? () => openOverviewFilingSection('filing-history')
              : dominantCompletionLabel === 'review filing section'
                ? () => openOverviewFilingSection('filing-form')
                : returnToHarvestOrLots,
    helper:
      dominantCompletionLabel === 'review transactions'
        ? 'Return to /transactions to remount source truth before this decision owner confirms export or filing.'
        : dominantCompletionLabel === 'review export proof'
        ? 'Keep the mounted export proof visible, then choose a download format on this route.'
        : dominantCompletionLabel === 'prepare report on /transactions'
          ? 'Use the existing tax report preparation surface, then return here for the canonical verdict.'
          : dominantCompletionLabel === 'retry filing'
            ? 'Review the mounted rejection locally, then correct and resubmit from this route.'
            : dominantCompletionLabel === 'review filed history'
              ? 'Use the mounted year/provider evidence below to confirm what was recorded.'
              : dominantCompletionLabel === 'review filing section'
                ? 'Keep the same year/provider proof visible while you review filing status on this route.'
                : 'Return to the mounted harvest or lot proof before filing readiness can recover.',
  };
  const recommendedTab: TopTab = sourceTruthRequired ? 'overview' : filingSurfaceOwnsPrimaryCTA ? 'overview' : dominantTaxRail.tab;
  const currentTabLabel = TOP_TAB_LABELS[activeTab];
  const recommendedTabLabel = TOP_TAB_LABELS[recommendedTab];
  const recommendedSurfaceLabel =
    sourceTruthRequired
      ? 'Source truth'
      : filingSurfaceOwnsPrimaryCTA
      ? completionOwnerMode === 'review-filed-history'
        ? 'Filing history'
        : completionOwnerMode === 'retry-filing'
          ? 'Filing retry'
          : 'Filing follow-up'
      : dominantTaxRail.step === 'review-lots'
        ? dominantTaxRail.focus === 'method-comparison'
          ? 'Method comparison'
          : 'Lot review'
        : dominantTaxRail.step === 'harvest'
          ? 'Harvest proof'
          : 'Export proof';
  const routeGuideMessage = sourceTruthRequired
    ? sourceTruthRequiredMessage
    : filingSurfaceOwnsPrimaryCTA
    ? completionFilingStatus.message
    : dominantTaxRail.message;
  const showRouteGuide = recommendedTab !== 'overview' || activeTab !== recommendedTab;
  const taxOptimisationOwnerCtaLabel =
    sourceTruthRequired
      ? 'Review transactions'
      : activeTab === 'export'
      ? completionDominantAction.label
      : dominantTaxRail.ctaLabel;
  const taxOptimisationProofLabel =
    sourceTruthRequired
      ? sourceTruthRequiredLabel
      : activeTab === 'export'
      ? completionProofLabel
      : carriedSourceProof?.sourceLabel ?? dominantTaxRail.proofLabel;
  const overviewOwnerHeadline = sourceTruthRequired ? sourceTruthRequiredLabel : overviewProofLabel;
  const overviewOwnerStatus = sourceTruthRequired
    ? {
      tone: 'warning' as const,
      title: 'Review transactions first',
      message: sourceTruthRequiredMessage,
    }
    : overviewProofHelper;
  const overviewDominantStepLabel = sourceTruthRequired ? 'Review transactions' : getTaxNextStepLabel(dominantTaxRail.step);
  const overviewWhyThisStepTitle = sourceTruthRequired ? sourceTruthRequiredLabel : dominantTaxRail.title;
  const overviewWhyThisStepMessage = sourceTruthRequired ? sourceTruthRequiredMessage : dominantTaxRail.message;
  const overviewWhyThisStepProof = sourceTruthRequired
    ? 'This decision owner will promote one dominant export or filing CTA only after /transactions remounts the carried source proof.'
    : dominantTaxRail.proofLabel;
  const overviewOwnerAction = sourceTruthRequired
    ? () => navigate('/transactions?view=taxReports')
    : () => navigateToTaxStep(dominantTaxRail);
  const taxSetupPrompt = activeTab === 'overview' && !overviewRailLoading
    ? sourceTruthRequired
      ? {
        title: 'Import or sync transactions before optimising taxes',
        message:
          'Bring transaction proof into /transactions so Pinecurl can mount ledger evidence, build tax lots, and unlock the next best harvest, export, or filing action here.',
        ctaLabel: 'Open transactions',
      }
      : noTaxLotsMounted
        ? {
          title: 'Add buy history to create your first tax lots',
          message:
            'Tax optimisation stays empty until Pinecurl can see at least one tax lot. Review transactions to add or reconcile the buys that should power harvesting, method comparison, and exports.',
          ctaLabel: 'Add transactions',
        }
        : null
    : null;
  const recommendationEvidenceHandoffHref = useMemo(() => {
    if (sourceTruthRequired) {
      return null;
    }

    const sharedContext = {
      handoff: 'recommendation-evidence' as const,
      source: 'tax-optimisation' as const,
      occurredAt: completionSurfaceProofTime ?? overviewProofTime ?? carriedSourceProof?.importedAt ?? null,
      year: readinessYear,
      provider: readinessProvider,
      method: activeMethod,
    };

    if (activeTab === 'export') {
      if (
        completionDominantAction.label === 'review transactions'
        || completionDominantAction.label === 'prepare report on /transactions'
      ) {
        return null;
      }

      return getRecommendationTransactionsHandoffPath({
        ...sharedContext,
        view: 'taxReports',
        focus: 'tax-report-slice',
        proofLabel: completionProofLabel,
        reasonLabel: 'Filing year',
        reasonValue: `${readinessYear}`,
      });
    }

    if (dominantTaxRail.step === 'export') {
      return getRecommendationTransactionsHandoffPath({
        ...sharedContext,
        view: 'taxReports',
        focus: 'tax-report-slice',
        proofLabel: dominantTaxRail.proofLabel,
        reasonLabel: 'Filing year',
        reasonValue: `${dominantTaxRail.year ?? readinessYear}`,
      });
    }

    return getRecommendationTransactionsHandoffPath({
      ...sharedContext,
      view: 'transactions',
      focus: 'lot-evidence',
      proofLabel: dominantTaxRail.proofLabel,
      reasonLabel: dominantTaxRail.focus === 'method-comparison' ? 'Method focus' : 'Lot focus',
      reasonValue:
        dominantTaxRail.focus === 'method-comparison'
          ? activeMethod.toUpperCase()
          : `${activeLotCount} lot${activeLotCount === 1 ? '' : 's'}`,
    });
  }, [
    activeLotCount,
    activeMethod,
    activeTab,
    carriedSourceProof?.importedAt,
    completionDominantAction.label,
    completionProofLabel,
    completionSurfaceProofTime,
    dominantTaxRail.focus,
    dominantTaxRail.proofLabel,
    dominantTaxRail.step,
    dominantTaxRail.year,
    overviewProofTime,
    readinessProvider,
    readinessYear,
    sourceTruthRequired,
  ]);
  const recommendationEvidenceHandoffTitle =
    activeTab === 'export' || dominantTaxRail.step === 'export'
      ? 'Open transaction evidence'
      : dominantTaxRail.focus === 'method-comparison'
        ? 'Inspect matching transactions'
        : 'Review supporting transactions';
  const recommendationEvidenceReasonLabel =
    activeTab === 'export' || dominantTaxRail.step === 'export'
      ? 'Filing year'
      : dominantTaxRail.focus === 'method-comparison'
        ? 'Method focus'
        : 'Lot focus';
  const recommendationEvidenceReasonValue =
    activeTab === 'export' || dominantTaxRail.step === 'export'
      ? `${readinessYear} · ${formatTaxFilingProviderLabel(readinessProvider)}`
      : dominantTaxRail.focus === 'method-comparison'
        ? activeMethod.toUpperCase()
        : `${activeLotCount} lot${activeLotCount === 1 ? '' : 's'}`;

  useEffect(() => {
    if (!user?.id || (activeTab !== 'overview' && activeTab !== 'export')) {
      return;
    }

    captureAuthReturnContext({
      pathname: location.pathname,
      search: location.search,
      ownerCtaLabel: taxOptimisationOwnerCtaLabel,
      proofLabel: taxOptimisationProofLabel,
      focus: 'tax-optimisation-owner-cta',
    });
  }, [
    activeTab,
    location.pathname,
    location.search,
    taxOptimisationOwnerCtaLabel,
    taxOptimisationProofLabel,
    user?.id,
  ]);

  useEffect(() => {
    if (authRecoveryState?.focus !== 'tax-optimisation-owner-cta') {
      return;
    }

    const target = document.getElementById('tax-optimisation-owner-cta');
    if (!target) {
      return;
    }

    target.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    if ('focus' in target && typeof target.focus === 'function') {
      target.focus();
    }

    const nextSearch = stripAuthRecoveryParams(location.search);
    navigate(`${location.pathname}${nextSearch}`, { replace: true });
  }, [authRecoveryState?.focus, location.pathname, location.search, navigate]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
          <ScalesIcon />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Tax Optimisation</h1>
          <p className="text-sm text-white/50 mt-0.5">
            Manage cost basis methods, harvest tax losses, and simulate year-end tax scenarios
          </p>
          <p className="mt-2 text-xs text-white/40">
            Display currency:{' '}
            <span className="font-mono tabular-nums text-white/80">{activeDisplayCurrency}</span>
          </p>
        </div>
      </div>

      {taxSetupPrompt ? (
        <section className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-3">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
                  What to do next
                </p>
                <h2 className="text-xl font-semibold text-white">{taxSetupPrompt.title}</h2>
                <p className="max-w-3xl text-sm leading-6 text-emerald-100/90">
                  {taxSetupPrompt.message}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className={`rounded-full border px-3 py-1 ${
                  sourceTruthRequired
                    ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
                    : 'border-emerald-400/30 bg-emerald-500/15 text-emerald-100'
                }`}>
                  1. Transactions proof
                </span>
                <span className={`rounded-full border px-3 py-1 ${
                  noTaxLotsMounted
                    ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
                    : 'border-emerald-400/30 bg-emerald-500/15 text-emerald-100'
                }`}>
                  2. Tax lots
                </span>
                <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-white/70">
                  3. Harvest, export, or file
                </span>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-3">
              <Link to="/transactions?view=taxReports" className="btn btn--outline text-sm">
                {taxSetupPrompt.ctaLabel}
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      {/* ── URL-Persisted Tab Bar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 border-b border-white/10 pb-0">
        {TOP_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            aria-label={tab.label}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-violet-500 text-violet-300 bg-violet-500/10'
                : 'border-transparent text-white/50 hover:text-white/80 hover:bg-white/5'
            }`}
          >
            <span className="inline-flex items-center gap-2">
              <span>{tab.label}</span>
              {recommendedTab === tab.key ? (
                <span
                  aria-hidden="true"
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    activeTab === tab.key
                      ? 'border-violet-400/50 bg-violet-500/20 text-violet-200'
                      : 'border-blue-400/40 bg-blue-500/10 text-blue-200'
                  }`}
                >
                  {activeTab === tab.key ? 'Now' : 'Next'}
                </span>
              ) : null}
            </span>
          </button>
        ))}
      </div>

      {showRouteGuide ? (
        <section className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wide text-white/40">Route guide</p>
              <p className="text-sm font-semibold text-white">
                Recommended now: <span className="text-violet-200">{recommendedTabLabel}</span> — {recommendedSurfaceLabel}
              </p>
              <p className="text-xs text-white/60">{routeGuideMessage}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-white/10 bg-gray-950/50 px-3 py-1 text-xs text-white/60">
                Current tab:{' '}
                <span className="font-medium text-white/80">{currentTabLabel}</span>
              </span>
              {activeTab !== recommendedTab ? (
                <button
                  type="button"
                  onClick={() => setActiveTab(recommendedTab)}
                  className="text-xs text-violet-200 underline-offset-4 transition-colors hover:text-violet-100 hover:underline"
                >
                  Jump to {recommendedTabLabel}
                </button>
              ) : (
                <span className="rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-xs text-violet-200">
                  You are on the recommended tab
                </span>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Tab: Tax-Loss Harvesting ──────────────────────────────────────────── */}
      {activeTab === 'harvesting' && (
        <Suspense fallback={<TaxSurfaceLoadingState label="Tax-loss harvesting" />}>
          <TaxHarvestPage handoff={taxHandoff?.tab === 'harvesting' ? taxHandoff : null} />
        </Suspense>
      )}

      {/* ── Tab: Simulator ───────────────────────────────────────────────────── */}
      {activeTab === 'simulator' && (
        <Suspense fallback={<TaxSurfaceLoadingState label="Tax simulator" />}>
          <TaxSimulatorPage />
        </Suspense>
      )}

      {activeTab === 'export' && (
        <div className="space-y-6">
          <TaxCompletionRail
            headline={completionProofLabel}
            proofTime={completionSurfaceProofTime ?? 'Exact proof time unavailable'}
            chips={completionChips}
            exportStatus={completionExportStatus}
            filingStatus={completionFilingStatus}
            historyItems={relatedHistoryEvidence.map((record) => ({
              id: record.id,
              provider: formatTaxFilingProviderLabel(record.provider),
              year: record.taxYear,
              status: formatFilingStatusChip(record.status),
              confirmationNumber: record.confirmationNumber,
              submittedAt: record.submittedAt,
            }))}
            historyLoading={filingHistoryLoading}
            historyEmptyMessage={`No filing history is mounted yet for export year ${exportSelectedYear}${yearsDiverged ? ` or filing year ${filingYear}` : ''}.`}
            dominantActionId="tax-optimisation-owner-cta"
            dominantAction={{
              ...completionDominantAction,
              variant: 'primary',
            }}
            helperStatus={completionHelperStatus}
            secondaryActions={sourceTruthRequired ? [] : completionSecondaryActions}
          />
          {carriedSourceFreshness ? (
            <CarriedSourceFreshnessRail
              snapshot={carriedSourceFreshness}
              decisionRoute="/tax-optimisation"
              title="Carried source freshness"
              testId="tax-optimisation-carried-source-freshness"
            />
          ) : null}
          {taxHandoff?.tab === 'export' ? (
            <TaxNextStepArrivalRail
              handoff={taxHandoff}
              title="Keep export proof visible before you download or file"
              message={taxHandoff.proofLabel}
            />
          ) : null}
          {recommendationEvidenceHandoffHref ? (
            <section className="rounded-xl border border-blue-500/20 bg-gray-950/50 p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300">
                    Supporting transaction evidence
                  </p>
                  <p className="text-sm font-semibold text-white">
                    Keep /tax-optimisation as the decision owner, then open the matching /transactions evidence for the same year and proof basis.
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-300">
                    <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1">
                      Source route:{' '}
                      <span className="font-mono tabular-nums text-gray-100">/tax-optimisation</span>
                    </span>
                    <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1">
                      Proof time:{' '}
                      <span className="font-mono tabular-nums text-gray-100">
                        {completionSurfaceProofTime ?? overviewProofTime ?? 'Exact proof time unavailable'}
                      </span>
                    </span>
                    <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1">
                      {recommendationEvidenceReasonLabel}:{' '}
                      <span className="font-mono tabular-nums text-gray-100">{recommendationEvidenceReasonValue}</span>
                    </span>
                  </div>
                </div>
                <Link to={recommendationEvidenceHandoffHref} className="btn btn--outline text-sm">
                  {recommendationEvidenceHandoffTitle}
                </Link>
              </div>
            </section>
          ) : null}
          <TaxExportPanel
            userId={user?.id}
            activeDisplayCurrency={activeDisplayCurrency}
            initialSelectedYear={selectedOverviewExportYear}
            handoff={taxHandoff?.tab === 'export' ? taxHandoff : null}
            onSnapshotChange={setExportPanelSnapshot}
            showArrivalRail={false}
          />
        </div>
      )}

      {/* ── Tab: Overview ────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {overviewRailLoading ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 space-y-4">
              <div className="space-y-2">
                <div className="h-4 w-32 rounded bg-white/5 animate-pulse" />
                <div className="h-6 w-72 rounded bg-white/5 animate-pulse" />
                <div className="h-4 w-full max-w-3xl rounded bg-white/5 animate-pulse" />
              </div>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-8 w-28 rounded-full bg-white/5 animate-pulse" />
                ))}
              </div>
            </div>
          ) : (
            <section className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-5 space-y-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-300">
                    Tax readiness owner
                  </p>
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold text-white">{overviewOwnerHeadline}</h2>
                    <p className="text-sm font-mono tabular-nums text-blue-100/90">
                      {sourceTruthRequired ? '/transactions source truth required' : overviewProofTime ?? 'Exact proof time unavailable'}
                    </p>
                  </div>
                </div>
                <div className="rounded-lg border border-blue-400/20 bg-gray-950/40 px-3 py-2 lg:min-w-[12rem]">
                  <p className="text-[11px] uppercase tracking-wide text-blue-200/80">Dominant step</p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    {overviewDominantStepLabel}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                  Method:{' '}
                  <span className="font-mono tabular-nums uppercase text-gray-100">{activeMethod}</span>
                </div>
                <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                  Jurisdiction:{' '}
                  <span className="font-mono tabular-nums text-gray-100">{jurisdiction}</span>
                </div>
                <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                  Currency:{' '}
                  <span className="font-mono tabular-nums text-gray-100">{activeDisplayCurrency}</span>
                </div>
                {dominantTaxRail.year !== undefined ? (
                  <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                    Year:{' '}
                    <span className="font-mono tabular-nums text-gray-100">{dominantTaxRail.year}</span>
                  </div>
                ) : null}
                {dominantTaxRail.proofCount !== undefined ? (
                  <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                    Proof count:{' '}
                    <span className="font-mono tabular-nums text-gray-100">{dominantTaxRail.proofCount}</span>
                  </div>
                ) : null}
                {dominantTaxRail.proofValue ? (
                  <div className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
                    Proof value:{' '}
                    <span className="font-mono tabular-nums text-gray-100">{dominantTaxRail.proofValue}</span>
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  id="tax-optimisation-owner-cta"
                  type="button"
                  onClick={overviewOwnerAction}
                  variant={sourceTruthRequired || overviewRailOwnsPrimaryCTA ? 'primary' : 'outline'}
                  size="sm"
                >
                  {sourceTruthRequired ? 'Review transactions' : dominantTaxRail.ctaLabel}
                </Button>
                {!sourceTruthRequired ? (
                  <div className="flex flex-wrap gap-2">
                    {dominantTaxRail.secondaryActions.map((action) => (
                      <button
                        key={action.key}
                        type="button"
                        onClick={() => {
                          if (action.key === 'export') {
                            openExportCompletion();
                            return;
                          }

                          setActiveTab(action.tab);
                        }}
                        className="text-xs text-white/50 underline-offset-4 transition-colors hover:text-white/80 hover:underline"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <InlineStatus
                tone={overviewOwnerStatus.tone}
                title={overviewOwnerStatus.title}
                message={overviewOwnerStatus.message}
                compact
              />

              <div className="rounded-lg border border-white/10 bg-gray-950/40 px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-white/40">Why this step won now</p>
                <p className="mt-1 text-sm font-semibold text-white">{overviewWhyThisStepTitle}</p>
                <p className="mt-2 text-sm text-blue-100/90">{overviewWhyThisStepMessage}</p>
                <p className="mt-2 text-sm text-white/80">{overviewWhyThisStepProof}</p>
              </div>

              {recommendationEvidenceHandoffHref ? (
                <div className="rounded-lg border border-blue-500/20 bg-gray-950/50 p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300">
                        Supporting transaction evidence
                      </p>
                      <p className="text-sm font-semibold text-white">
                        Keep this decision owner in front, then open the matching /transactions evidence for the same mounted tax proof.
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-300">
                        <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1">
                          Source route:{' '}
                          <span className="font-mono tabular-nums text-gray-100">/tax-optimisation</span>
                        </span>
                        <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1">
                          Proof time:{' '}
                          <span className="font-mono tabular-nums text-gray-100">
                            {overviewProofTime ?? completionSurfaceProofTime ?? 'Exact proof time unavailable'}
                          </span>
                        </span>
                        <span className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1">
                          {recommendationEvidenceReasonLabel}:{' '}
                          <span className="font-mono tabular-nums text-gray-100">{recommendationEvidenceReasonValue}</span>
                        </span>
                      </div>
                    </div>
                    <Link to={recommendationEvidenceHandoffHref} className="btn btn--outline text-sm">
                      {recommendationEvidenceHandoffTitle}
                    </Link>
                  </div>
                </div>
              ) : null}
            </section>
          )}
          {carriedSourceFreshness ? (
            <CarriedSourceFreshnessRail
              snapshot={carriedSourceFreshness}
              decisionRoute="/tax-optimisation"
              title="Carried source freshness"
              testId="tax-optimisation-carried-source-freshness"
            />
          ) : null}
          {taxHandoff?.tab === 'overview' && taxHandoff.step === 'review-lots' ? (
            <TaxNextStepArrivalRail
              handoff={taxHandoff}
              title={
                taxHandoff.focus === 'method-comparison'
                  ? 'Keep the method proof visible while you compare cost basis'
                  : 'Keep the lot proof visible while you review ledger order'
              }
              message={taxHandoff.proofLabel}
            />
          ) : null}

       {/* ── Real Tax Estimate Banner ──────────────────────────────────────────── */}
      {taxReportLoading ? (
        <div className="h-20 rounded-xl bg-white/5 animate-pulse" />
      ) : taxReport ? (
        <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Net Gain / Loss</p>
            <p className={`text-xl font-bold font-mono ${taxReport.summary.netGain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatCurrency(taxReport.summary.netGain)}
            </p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Short-Term Gains</p>
            <p className="text-xl font-bold font-mono text-amber-400">{formatCurrency(taxReport.summary.shortTermGain)}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Long-Term Gains</p>
            <p className="text-xl font-bold font-mono text-blue-400">{formatCurrency(taxReport.summary.longTermGain)}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Ordinary Income</p>
            <p className="text-xl font-bold font-mono text-white/80">{formatCurrency(taxReport.summary.totalOrdinaryIncome)}</p>
          </div>
          <p className="col-span-full text-xs text-white/30 pt-2 border-t border-white/5">
            Based on {taxReport.summary.transactionCount} transactions · {taxReport.method} method · Tax year {taxReport.yearFrom}–{taxReport.yearTo}
          </p>
        </div>
      ) : taxReportError ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-400">
          {taxReportError} — generate a tax report to see estimates.
        </div>
      ) : null}

      {/* ── Disclaimer ─────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 flex items-start gap-3">
        <span className="text-amber-400 mt-0.5 shrink-0 text-base">⚠️</span>
        <p className="text-sm text-amber-200 leading-relaxed">
          <strong>Not tax advice — consult a qualified tax professional in your jurisdiction.</strong>{' '}
          This tool is for informational purposes only. Tax laws are complex, jurisdiction-specific,
          and subject to change. The calculations shown are approximations and may not reflect your
          actual tax liability.
        </p>
      </div>

      {/* ── Canonical export handoff ─────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white">Tax export</h2>
            <p className="text-sm text-white/70">
              The Export tab is the canonical filing surface for Form 8949, TurboTax, and Generic CSV.
              It keeps year, format, reporting currency, and inline progress/error states visible throughout the run.
            </p>
            <p className="text-xs text-white/40">
              Current display currency:{' '}
              <span className="font-mono tabular-nums text-white/70">{activeDisplayCurrency}</span>
            </p>
          </div>
          <Button
            type="button"
            onClick={openExportCompletion}
            variant="outline"
            size="sm"
          >
            Open export proof
          </Button>
        </div>
      </section>

      {/* ── Tax Jurisdiction selector ──────────────────────────────────────── */}
      <section className="rounded-xl border border-border bg-surface p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1.5">
              Tax Jurisdiction
            </label>
            <div className="flex items-center gap-3">
              <select
                value={jurisdiction}
                onChange={(e) => void handleJurisdictionChange(e.target.value as TaxJurisdiction)}
                disabled={jurisdictionSaving}
                className="px-3 py-2 rounded-lg bg-surface-active border border-border text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-60"
                aria-label="Tax jurisdiction selector"
              >
                {JURISDICTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {jurisdictionStatus && (
              <div className="mt-3">
                <InlineStatus
                  tone={jurisdictionStatus.tone}
                  title={jurisdictionStatus.title}
                  message={jurisdictionStatus.message}
                  compact
                  action={jurisdictionStatus.tone === 'error' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleJurisdictionChange(jurisdiction)}
                    >
                      Retry save
                    </Button>
                  ) : undefined}
                />
              </div>
            )}
          </div>
          <div className="flex-1 sm:border-l sm:border-border sm:pl-5">
            <p className="text-xs text-text-muted leading-relaxed">
              {JURISDICTION_NOTES[jurisdiction]}
            </p>
          </div>
        </div>
      </section>

      {/* Potential saving banner */}
      {potentialSaving !== null && potentialSaving > 0 && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-300">
              💡 Potential tax saving vs FIFO
            </p>
            <p className="text-xs text-emerald-400/70 mt-0.5">
              Using HIFO instead of FIFO could reduce your unrealised gains by{' '}
              <strong>{potentialSaving != null ? formatCurrency(potentialSaving) : '–'}</strong>, deferring tax liability.
            </p>
          </div>
          <span className="text-2xl font-bold text-emerald-300">{potentialSaving != null ? formatCurrency(potentialSaving) : '–'}</span>
        </div>
      )}

      {/* Side-by-side comparison */}
      <section
        ref={reviewMethodComparisonRef}
        tabIndex={-1}
        className="focus:outline-none focus:ring-1 focus:ring-blue-500/40"
      >
        <div className="mb-4 space-y-1">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
            Method Comparison
          </h2>
          <p className="text-xs text-white/40">
            Select a method card to update the lot ledger below without losing the comparison proof.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {METHODS.map((m) => (
            <ComparisonCard
              key={m.value}
              method={m}
              totalUnrealisedGainLoss={methodSummaries[m.value]}
              isLoading={
                m.value === 'fifo' ? fifo.isLoading : m.value === 'lifo' ? lifo.isLoading : hifo.isLoading
              }
              isActive={m.value === activeMethod}
              formatCurrency={formatCurrency}
              onSelect={() => setActiveMethod(m.value)}
            />
          ))}
        </div>
      </section>

      {/* Method selector + lot table */}
      <section
        ref={reviewLotLedgerRef}
        tabIndex={-1}
        className="focus:outline-none focus:ring-1 focus:ring-blue-500/40"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
            Lot Order — {METHODS.find((m) => m.value === activeMethod)?.label}
          </h2>
          <div className="flex gap-1 rounded-lg bg-white/5 p-1 border border-white/10">
            {METHODS.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setActiveMethod(m.value)}
                aria-pressed={activeMethod === m.value}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  activeMethod === m.value
                    ? 'bg-violet-600 text-white shadow'
                    : 'text-white/50 hover:text-white/80'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading skeleton */}
        {activeLots.isLoading && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 rounded-lg bg-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {/* Error */}
        {!activeLots.isLoading && activeLots.error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center">
            <p className="text-red-400 font-medium">{activeLots.error}</p>
            <button
              onClick={activeLots.refetch}
              className="btn btn--ghost mt-3 text-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty */}
        {!activeLots.isLoading && !activeLots.error && activeLots.data?.lots.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-12 text-center">
            <p className="text-white/70 text-sm font-medium">No tax lots found yet.</p>
            <p className="mt-1 text-xs text-white/40">
              Lots are created from your transaction history. Add or reconcile buys in Transactions to unlock comparisons, harvesting, and export steps here.
            </p>
            <div className="mt-4 flex justify-center">
              <Link to="/transactions?view=taxReports" className="btn btn--primary text-sm">
                Open transactions
              </Link>
            </div>
          </div>
        )}

        {/* Lot table */}
        {!activeLots.isLoading && !activeLots.error && (activeLots.data?.lots.length ?? 0) > 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-x-auto">
            {/* Summary strip */}
            <div className="grid grid-cols-3 gap-px border-b border-white/10 bg-white/5">
              {[
                { label: 'Total Cost Basis', value: formatCurrency(activeLots.data!.summary.totalCostBasis ?? 0) },
                { label: 'Current Value', value: formatCurrency(activeLots.data!.summary.totalCurrentValue ?? 0) },
                {
                  label: 'Unrealised P/L',
                  value: formatCurrency(activeLots.data!.summary.totalUnrealisedGainLoss ?? 0),
                  positive: (activeLots.data!.summary.totalUnrealisedGainLoss ?? 0) >= 0,
                },
              ].map((stat) => (
                <div key={stat.label} className="px-5 py-3 bg-white/[0.02]">
                  <p className="text-xs text-white/40">{stat.label}</p>
                  <p
                    className={`text-base font-bold mt-0.5 ${
                      'positive' in stat
                        ? stat.positive
                          ? 'text-emerald-400'
                          : 'text-red-400'
                        : 'text-white'
                    }`}
                  >
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            <table className="w-full text-left">
              <thead>
                <tr className="text-xs text-white/30 font-medium uppercase tracking-wider">
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Amount</th>
                  <th className="py-3 px-4">Cost / Unit</th>
                  <th className="py-3 px-4">Total Cost</th>
                  <th className="py-3 px-4">Current Value</th>
                  <th className="py-3 px-4">Unrealised G/L</th>
                  <th className="py-3 px-4">Holding Period</th>
                  <th className="py-3 px-4">Acquired</th>
                </tr>
              </thead>
              <tbody>
                {activeLots.data!.lots.map((lot, i) => (
                  <LotRow key={lot.lotId} lot={lot} rank={i + 1} formatCurrency={formatCurrency} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Ordinary Income Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
            Ordinary Income
          </h2>
          <div className="flex items-center gap-2">
            <label className="text-xs text-white/40">Tax Year:</label>
            <select
              value={incomeYear}
              onChange={(e) => setIncomeYear(parseInt(e.target.value))}
              className="text-xs bg-white/5 border border-white/10 rounded px-2 py-1 text-white/80"
            >
              {[...Array(5)].map((_, i) => {
                const yr = new Date().getFullYear() - i;
                return <option key={yr} value={yr}>{yr}</option>;
              })}
            </select>
          </div>
        </div>

        {incomeLoading && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-10 rounded-lg bg-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {!incomeLoading && incomeError && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-center">
            <p className="text-red-400 text-sm">{incomeError}</p>
          </div>
        )}

        {!incomeLoading && !incomeError && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
            {/* Total banner */}
            <div className="px-5 py-4 bg-white/[0.02] border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs text-white/40">Total Ordinary Income — {incomeYear}</p>
                <p className="text-2xl font-bold text-emerald-400 mt-0.5">
                  {formatCurrency(incomeData?.totalOrdinaryIncome ?? 0)}
                </p>
              </div>
              <p className="text-xs text-white/30">
                {incomeData?.events.length ?? 0} income event{(incomeData?.events.length ?? 0) !== 1 ? 's' : ''}
              </p>
            </div>

            {incomeData && Object.keys(incomeData.byType).length > 0 ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs text-white/30 font-medium uppercase tracking-wider">
                    <th className="py-3 px-4">Income Type</th>
                    <th className="py-3 px-4 text-right">Events</th>
                    <th className="py-3 px-4 text-right">Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(incomeData.byType).map(([type, stats]) => (
                    <tr key={type} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-500/15 text-teal-300 border border-teal-500/30">
                            {INCOME_TYPE_LABELS[type] ?? type}
                          </span>
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-white/60 text-right font-mono">{stats.count}</td>
                      <td className="py-3 px-4 text-sm font-semibold text-emerald-400 text-right font-mono">
                        {formatCurrency(stats.totalUsd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center">
                <p className="text-white/40 text-sm">No income events for {incomeYear}.</p>
                <p className="text-white/25 text-xs mt-1">
                  Label transactions as staking rewards, airdrops, gifts, etc. in the Transaction Explorer to track ordinary income.
                </p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── File Your Taxes ─────────────────────────────────────────────────── */}
      <section
        ref={filingSectionRef}
        tabIndex={-1}
        className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-6 space-y-5 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
      >
        <TaxCompletionRail
          headline={completionProofLabel}
          proofTime={completionSurfaceProofTime ?? 'Exact proof time unavailable'}
          chips={completionChips}
          exportStatus={completionExportStatus}
          filingStatus={completionFilingStatus}
          historyItems={relatedHistoryEvidence.map((record) => ({
            id: record.id,
            provider: formatTaxFilingProviderLabel(record.provider),
            year: record.taxYear,
            status: formatFilingStatusChip(record.status),
            confirmationNumber: record.confirmationNumber,
            submittedAt: record.submittedAt,
          }))}
          historyLoading={filingHistoryLoading}
          historyEmptyMessage={`No filing history is mounted yet for export year ${exportSelectedYear}${yearsDiverged ? ` or filing year ${filingYear}` : ''}.`}
          dominantAction={{
            ...completionDominantAction,
            variant: sourceTruthRequired || filingSurfaceOwnsPrimaryCTA ? 'primary' : 'outline',
          }}
          helperStatus={completionHelperStatus}
          secondaryActions={sourceTruthRequired ? [] : completionSecondaryActions}
          className="mb-1"
        />
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-white">File Your Taxes</h2>
            <p className="text-xs text-white/50 mt-0.5">Submit your crypto gains directly to TurboTax or HMRC, or download a universal CSV for CoinTracker / Koinly / TaxBit.</p>
          </div>
        </div>

        {/* Provider selector + year */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-white/40 mb-2 font-medium uppercase tracking-wide">Filing Provider</p>
            <div role="radiogroup" aria-label="Filing provider mode" className="grid gap-2 sm:grid-cols-3">
              {FILING_PROVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={filingProvider === option.value}
                  aria-label={`Select ${option.label} mode`}
                  onClick={() => setFilingProvider(option.value)}
                  className={`rounded-lg border px-3 py-3 text-left transition-all ${
                    filingProvider === option.value
                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-200'
                      : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.06] hover:text-white/85'
                  }`}
                >
                  <span className="flex items-start gap-2">
                    <span className="text-base leading-none" aria-hidden="true">{option.icon}</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-current">{option.label}</span>
                      <span className="mt-1 block text-xs text-white/45">{option.detail}</span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-white/45">
              Choose a provider here, then use the action button below once the selected proof is ready.
            </p>
            <p className="mt-1 text-xs text-blue-100/70">
              {filingProviderSelectionHint}
            </p>
          </div>

          <div>
            <p className="text-xs text-white/40 mb-2 font-medium uppercase tracking-wide">Tax Year</p>
            <select
              value={filingYear}
              onChange={(e) => {
                setFilingYearTouched(true);
                setFilingYear(Number(e.target.value));
              }}
              className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/50"
            >
              {FILING_YEAR_OPTIONS.map((yr) => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
        </div>

        {yearsDiverged ? (
          <InlineStatus
            tone="info"
            title="Export and filing years differ"
            message={`Export proof is mounted for ${exportSelectedYear} while the filing controls are set to ${filingYear}. Pinecurl keeps both explicit because you chose a different filing year.`}
            compact
          />
        ) : null}

        {/* HMRC token input */}
        {filingProvider === 'hmrc' && (
          <div>
            <p className="text-xs text-white/40 mb-2 font-medium uppercase tracking-wide">HMRC OAuth2 Access Token</p>
            <input
              type="password"
              value={hmrcToken}
              onChange={(e) => setHmrcToken(e.target.value)}
              placeholder="Bearer token from HMRC developer OAuth2 flow"
              className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/25 focus:outline-none focus:border-blue-500/50"
            />
            <p className="text-xs text-white/30 mt-1">
              Obtain this from the HMRC Developer Hub OAuth2 flow. Never share this token.
            </p>
          </div>
        )}

        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 space-y-4">
          <p className="text-xs text-white/40 font-medium uppercase tracking-wide">Mounted local proof</p>

          {activeFilingEvidence ? (
            <InlineStatus
              tone={
                activeFilingEvidence.status === 'accepted'
                  ? 'success'
                  : activeFilingEvidence.status === 'rejected'
                  ? 'error'
                  : 'info'
              }
              title={
                activeFilingEvidence.status === 'accepted'
                  ? 'Filing confirmation is mounted'
                  : activeFilingEvidence.status === 'rejected'
                  ? 'Provider rejection is mounted'
                  : 'Filing response is mounted'
              }
              message={
                activeFilingEvidence.status === 'rejected'
                  ? activeFilingEvidence.errorMessage ?? `The ${formatTaxFilingProviderLabel(activeFilingEvidence.provider)} filing for ${activeFilingEvidence.taxYear} needs another review.`
                  : activeFilingEvidence.status === 'accepted'
                  ? `${formatTaxFilingProviderLabel(activeFilingEvidence.provider)} accepted the ${activeFilingEvidence.taxYear} filing proof on this route.`
                  : `${formatTaxFilingProviderLabel(activeFilingEvidence.provider)} recorded ${formatFilingStatusChip(activeFilingEvidence.status).toLowerCase()} status for ${activeFilingEvidence.taxYear}.`
              }
              meta={[
                `Year ${activeFilingEvidence.taxYear}`,
                `Provider ${formatTaxFilingProviderLabel(activeFilingEvidence.provider)}`,
                activeFilingEvidence.confirmationNumber
                  ? `Confirmation ${activeFilingEvidence.confirmationNumber}`
                  : null,
                formatSourceProofOccurredAt(activeFilingEvidence.submittedAt ?? activeFilingEvidence.updatedAt),
              ].filter(Boolean).join(' · ')}
              compact
            />
          ) : filingProvider === 'csv' ? (
            csvStoredReportReady ? (
              <InlineStatus
                tone="success"
                title="Stored report is mounted for CSV download"
                message={`Pinecurl will build the ${filingYear} CSV download from the stored ${matchingStoredCsvReport?.method} report on this account. CSV stays download-only proof.`}
                meta={[
                  `Year ${filingYear}`,
                  `Rows ${matchingStoredCsvTransactions.length}`,
                  matchingStoredCsvReport ? `Net ${formatCurrency(matchingStoredCsvReport.summary.netGain)}` : null,
                  formatSourceProofOccurredAt(matchingStoredCsvReport?.generatedAt ?? null),
                ].filter(Boolean).join(' · ')}
                compact
              />
            ) : taxReportLoading && taxReports.length === 0 ? (
              <InlineStatus
                tone="info"
                title="Checking stored tax reports"
                message={`Pinecurl is rechecking stored ${filingYear} tax reports before it offers CSV download-only proof on this surface.`}
                compact
              />
            ) : taxReportError && taxReports.length === 0 ? (
              <InlineStatus
                tone="warning"
                title="Stored report proof is unavailable"
                message={`${taxReportError}. Pinecurl cannot truthfully offer CSV completion here until the stored report list reloads.`}
                compact
              />
            ) : matchingStoredCsvReport ? (
              <InlineStatus
                tone="warning"
                title="Stored report has no verified CSV rows"
                message={`The stored ${filingYear} report is mounted, but it does not contain realised transaction rows Pinecurl can reuse for download-only CSV proof.`}
                compact
              />
            ) : (
              <InlineStatus
                tone="warning"
                title="No stored report for the selected year"
                message={`Create or reopen a ${filingYear} tax report on /transactions before Pinecurl can offer CSV download-only proof here.`}
                compact
              />
            )
          ) : hasMountedExportProofForFilingYear ? (
            <InlineStatus
              tone="info"
              title="Mounted export proof will back this submission"
              message={`${activeRailExportSummary?.disposalCount ?? 0} realised disposal${activeRailExportSummary?.disposalCount === 1 ? '' : 's'} are mounted for ${filingYear}, so Pinecurl can keep the provider action truthful on this surface.`}
              meta={[
                `Year ${filingYear}`,
                `Provider ${formatTaxFilingProviderLabel(filingProvider)}`,
                `Currency ${exportSelectedCurrency}`,
                completionProofTime,
              ].filter(Boolean).join(' · ')}
              compact
            />
          ) : exportPanelSnapshot?.summaryLoading ? (
            <InlineStatus
              tone="info"
              title="Rechecking export proof"
              message={`Pinecurl is rechecking whether ${filingYear} export proof is mounted before it enables ${formatTaxFilingProviderLabel(filingProvider)} submission here.`}
              compact
            />
          ) : (
            <InlineStatus
              tone="warning"
              title="No export proof is mounted for this filing year"
              message={`Open the export owner surface and confirm a ${filingYear} dataset with realised disposals before filing to ${formatTaxFilingProviderLabel(filingProvider)}.`}
              compact
            />
          )}

          {(activeFilingEvidence || csvStoredReportReady || hasMountedExportProofForFilingYear) ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-white/35">Year</p>
                <p className="mt-1 font-mono text-sm text-white">{filingYear}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-white/35">Provider</p>
                <p className="mt-1 font-mono text-sm text-white">{formatTaxFilingProviderLabel(filingProvider)}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-white/35">
                  {filingProvider === 'csv' ? 'Rows' : 'Currency'}
                </p>
                <p className="mt-1 font-mono text-sm text-white">
                  {filingProvider === 'csv'
                    ? matchingStoredCsvTransactions.length
                    : exportSelectedCurrency}
                </p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-white/35">
                  {activeFilingEvidence?.confirmationNumber ? 'Confirmation' : 'Proof time'}
                </p>
                <p className="mt-1 font-mono text-sm text-white">
                  {activeFilingEvidence?.confirmationNumber
                    ? activeFilingEvidence.confirmationNumber
                    : filingProvider === 'csv'
                    ? formatSourceProofOccurredAt(matchingStoredCsvReport?.generatedAt ?? null) ?? 'Exact proof time unavailable'
                    : completionProofTime ?? 'Exact proof time unavailable'}
                </p>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Button
              onClick={() => void handleFilingSubmit()}
              disabled={filingLoading || !user?.id || (filingProvider === 'hmrc' && !hmrcToken) || filingActionBlocked}
              disabledReason={filingActionBlockedReason ?? undefined}
              variant="outline"
              size="sm"
              isLoading={filingLoading}
              loadingLabel={filingProvider === 'csv' ? 'Preparing…' : 'Submitting…'}
            >
              {filingProvider === 'csv'
                ? 'Download CSV'
                : filingProvider === 'turbotax'
                ? 'Submit to TurboTax'
                : 'Submit to HMRC MTD'}
            </Button>

            <div className="flex flex-wrap gap-3 text-xs">
              {filingProvider === 'csv' && !csvStoredReportReady ? (
                <Link
                  to="/transactions"
                  className="text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline"
                >
                  Prepare report on /transactions
                </Link>
              ) : null}
              {(filingProvider === 'turbotax' || filingProvider === 'hmrc') && !activeFilingEvidence && !hasMountedExportProofForFilingYear ? (
                <button
                  type="button"
                  onClick={openExportCompletion}
                  className="text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline"
                >
                  Review export proof
                </button>
              ) : null}
            </div>
          </div>
          {filingActionBlockedReason ? (
            <p className="text-xs text-white/50">
              {filingActionBlockedReason}
            </p>
          ) : null}

          {filingStatus ? (
            <InlineStatus
              tone={filingStatus.tone}
              title={filingStatus.title}
              message={filingStatus.message}
              compact
            />
          ) : null}
        </div>

        {/* Filing history */}
        {filingRecords.length > 0 && (
          <div ref={filingHistoryRef} tabIndex={-1} className="focus:outline-none focus:ring-1 focus:ring-blue-500/40 rounded-lg">
            <p className="text-xs text-white/40 mb-2 font-medium uppercase tracking-wide">Filing History</p>
            {filingHistoryLoading ? (
              <div className="h-8 rounded bg-white/5 animate-pulse" />
            ) : (
              <div className="rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-xs text-white/30 border-b border-white/10">
                      <th className="py-2 px-3">Provider</th>
                      <th className="py-2 px-3">Year</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3">Confirmation</th>
                      <th className="py-2 px-3 text-right">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filingRecords.slice(0, 5).map((f) => (
                      <tr key={f.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
                        <td className="py-2 px-3 text-sm text-white/70 uppercase font-medium">{f.provider}</td>
                        <td className="py-2 px-3 text-sm text-white/60 font-mono">{f.taxYear}</td>
                        <td className={`py-2 px-3 text-xs font-medium ${filingStatusColor(f.status)}`}>
                          {f.status}
                        </td>
                        <td className="py-2 px-3 text-xs text-white/40 font-mono">
                          {f.confirmationNumber ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-xs text-white/40 text-right">
                          {f.submittedAt ? new Date(f.submittedAt).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Explainer */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-6 space-y-3">
        <h2 className="text-sm font-semibold text-white/70">Understanding cost basis methods</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/50">
          <div>
            <p className="font-semibold text-white/70 mb-1">FIFO — First In, First Out</p>
            <p>The oldest lots are sold first. Default method in most jurisdictions. Typically results in the highest taxable gain during bull markets as early lots have lower cost basis.</p>
          </div>
          <div>
            <p className="font-semibold text-white/70 mb-1">LIFO — Last In, First Out</p>
            <p>The most recently acquired lots are sold first. Can reduce gains during rising markets by using higher-cost recent lots. Not allowed in some jurisdictions (e.g., UK).</p>
          </div>
          <div>
            <p className="font-semibold text-white/70 mb-1">HIFO — Highest In, First Out</p>
            <p>The highest-cost lots are sold first, minimising realised gain (or maximising realised loss). Optimal for tax minimisation where permitted. Consult your tax advisor.</p>
          </div>
        </div>
        <p className="text-xs text-white/30 pt-2 border-t border-white/5">
          ⚠️ This tool is for informational purposes only and does not constitute tax advice. Consult a qualified tax professional before making decisions based on cost basis method.
        </p>
      </section>
        </div>
      )}
    </div>
  );
}
